/**
 * Arabic UI strings — the default language (RTL). English (`en.ts`) must have exactly the same
 * keys: the type checker refuses a missing or extra key, and a test refuses empty strings or a
 * `{{placeholder}}` present in one language only.
 */
export const ar = {
  app: {
    name: 'دوب بركر',
    product: 'نظام إدارة المؤسسة',
    skipToContent: 'انتقل إلى المحتوى',
  },
  common: {
    save: 'حفظ',
    cancel: 'إلغاء',
    close: 'إغلاق',
    confirm: 'تأكيد',
    continue: 'متابعة',
    back: 'رجوع',
    loading: 'جارٍ التحميل…',
    retry: 'إعادة المحاولة',
    search: 'بحث',
    details: 'التفاصيل',
    actions: 'إجراءات',
    status: 'الحالة',
    name: 'الاسم',
    nameAr: 'الاسم بالعربي',
    nameEn: 'الاسم بالإنجليزي',
    phone: 'رقم الهاتف',
    email: 'البريد الإلكتروني',
    optional: 'اختياري',
    required: 'مطلوب',
    reason: 'السبب',
    reasonHint: 'اكتب السبب بوضوح (5 أحرف على الأقل). يُحفظ في سجل التدقيق.',
    none: 'لا يوجد',
    all: 'الكل',
    yes: 'نعم',
    no: 'لا',
    copy: 'نسخ',
    copied: 'تم النسخ',
    noResults: 'لا توجد نتائج.',
    comingSoon: 'قريباً',
    notProvided: 'غير متوفر بعد',
    companyWide: 'كل الشركة',
    unexpectedError: 'حدث خطأ غير متوقع. حاول مرة أخرى.',
    networkError: 'تعذّر الاتصال بالخادم. تأكد من الإنترنت وحاول مرة أخرى.',
    errorLabel: 'خطأ',
    warningLabel: 'تنبيه',
    successLabel: 'تم',
    infoLabel: 'معلومة',
    requestId: 'رقم الطلب: {{id}}',
    stub: 'تجريبي (غير مُفعّل)',
    formNotSaved: 'لم يتم الحفظ: يوجد خطأ في حقل واحد أو أكثر',
    formNotSent: 'لم يتم الإرسال: يوجد خطأ في حقل واحد أو أكثر',
    iqd: 'د.ع',
    formFieldsList: 'الحقول: {{fields}}',
  },
  theme: {
    label: 'المظهر',
    system: 'حسب الجهاز',
    light: 'فاتح',
    dark: 'داكن',
  },
  language: {
    label: 'اللغة',
    switchTo: 'English',
    ar: 'العربية',
    en: 'English',
  },
  desktop: {
    build: 'Dob Desktop {{version}}',
    serverStaging: 'خادم التجربة',
    serverProduction: 'الخادم الرسمي',
    serverDevelopment: 'خادم التطوير على هذا الجهاز',
    unsigned: 'نسخة تجريبية غير موقّعة',
  },
  connection: {
    checking: 'جارٍ التحقق من الاتصال…',
    online: 'متصل',
    offline: 'لا يوجد إنترنت',
    serverDown: 'الخادم لا يستجيب',
    onlineOnly:
      'لوحة الإدارة تعمل مع الإنترنت فقط. العمليات اليومية في الفروع تعمل بدون إنترنت من برنامج Dob Desktop.',
    desktopNotRegistered:
      'هذا الجهاز لم يُسجَّل في النظام بعد، لذلك يعمل مع الإنترنت فقط ولا يحفظ عمليات بدون إنترنت.',
    pending: 'لم تُرسل بعد: {{count}}',
    rejected: 'مرفوضة تحتاج مراجعة: {{count}}',
    allSent: 'كل العمليات أُرسلت',
  },
  auth: {
    signInTitle: 'تسجيل الدخول',
    signInSubtitle: 'لوحة إدارة دوب بركر',
    identifier: 'رقم الهاتف أو البريد الإلكتروني',
    identifierHint: 'مثال: 07701234567',
    password: 'كلمة المرور',
    showPassword: 'إظهار كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',
    signIn: 'دخول',
    signingIn: 'جارٍ الدخول…',
    forgotPassword: 'نسيت كلمة المرور؟',
    signOut: 'تسجيل الخروج',
    sessionEnded: 'انتهت الجلسة. سجّل الدخول مرة أخرى.',
    idleSignedOut:
      'خرجنا من حسابك تلقائيًا لأن هذا الجهاز لم يُستخدم لفترة، حمايةً لحسابك. سجّل الدخول مرة أخرى.',
    mfaTitle: 'التحقق بخطوتين',
    mfaSubtitle: 'افتح تطبيق المصادقة (مثل Google Authenticator) واكتب الرمز المكوّن من 6 أرقام.',
    code: 'الرمز',
    verify: 'تحقق',
    useRecoveryCode: 'لا أملك الهاتف — استخدم رمز استرداد',
    useAuthenticator: 'استخدم تطبيق المصادقة',
    recoveryCode: 'رمز الاسترداد',
    recoveryCodeHint: 'أحد الرموز العشرة التي حفظتها عند التفعيل. كل رمز يعمل مرة واحدة.',
    setupTitle: 'تفعيل التحقق بخطوتين',
    setupIntro: 'حسابك من حسابات الإدارة، لذلك يجب حمايته بتطبيق مصادقة على هاتفك.',
    setupStep1: 'ثبّت على هاتفك تطبيق Google Authenticator أو Microsoft Authenticator.',
    setupStep2: 'في التطبيق اضغط (+) ثم «مسح رمز QR» ووجّه الكاميرا إلى الرمز أدناه.',
    setupStep3: 'اكتب الرمز المكوّن من 6 أرقام الذي يظهر في التطبيق.',
    setupManual: 'لا تستطيع المسح؟ أدخل هذا المفتاح يدوياً:',
    qrAlt: 'رمز QR لإضافة الحساب إلى تطبيق المصادقة',
    recoveryTitle: 'رموز الاسترداد',
    recoveryIntro:
      'احفظ هذه الرموز العشرة في مكان آمن (ورقة في مكان مقفل). إذا ضاع هاتفك، كل رمز يسمح لك بالدخول مرة واحدة.',
    recoveryWarning: 'لن تظهر هذه الرموز مرة أخرى.',
    recoverySaved: 'حفظت الرموز في مكان آمن',
    recoveryDownload: 'تنزيل كملف نصي',
    recoveryFileTitle: 'رموز الاسترداد — دوب بركر',
    forgotTitle: 'استعادة كلمة المرور',
    forgotIntro: 'اكتب رقم هاتفك. سنرسل لك رمزاً من 6 أرقام عبر واتساب (أو البريد الإلكتروني).',
    channel: 'طريقة الإرسال',
    channelWhatsapp: 'واتساب',
    channelEmail: 'البريد الإلكتروني',
    sendCode: 'أرسل الرمز',
    codeSent: 'إذا كان الحساب موجوداً فقد أُرسل الرمز. صالح لمدة 10 دقائق.',
    newPassword: 'كلمة المرور الجديدة',
    repeatPassword: 'أعد كتابة كلمة المرور',
    passwordsDiffer: 'كلمتا المرور غير متطابقتين.',
    passwordRules:
      '8 أحرف على الأقل (16 لحسابات الإدارة التي تدخل بدون إنترنت)، ولا تستخدم رقم هاتفك أو كلمات سهلة مثل 12345678.',
    passwordRulesLong:
      '16 حرفاً على الأقل، ولا تستخدم رقم هاتفك أو كلمات سهلة. عدة كلمات بينها مسافات سهلة التذكّر.',
    longPasswordTitle: 'يجب أن تكون كلمة المرور 16 حرفاً على الأقل',
    longPasswordBody:
      'حسابك إداري ويمكنه الدخول بدون إنترنت على أجهزة الفروع، لذلك تُحفظ نسخة محمية من كلمة المرور على الجهاز الرئيسي في الفرع. كلمة المرور الطويلة تحمي هذه النسخة إذا سُرق الجهاز.',
    mustChangeLongTitle: 'اختر كلمة مرور أطول',
    mustChangeLongIntro: 'حسابك يحتاج كلمة مرور من 16 حرفاً على الأقل قبل أن تتابع.',
    setPassword: 'احفظ كلمة المرور',
    passwordReset: 'تم تغيير كلمة المرور. سجّل الدخول بها الآن.',
    backToSignIn: 'العودة لتسجيل الدخول',
    mustChangeTitle: 'غيّر كلمة المرور المؤقتة',
    mustChangeIntro: 'هذه أول مرة تدخل فيها بكلمة مرور مؤقتة. اختر كلمة مرور خاصة بك.',
    currentPassword: 'كلمة المرور الحالية',
    currentPasswordWrong: 'كلمة المرور الحالية غير صحيحة.',
    changePassword: 'تغيير كلمة المرور',
    passwordChanged: 'تم تغيير كلمة المرور. تم تسجيل خروج أجهزتك الأخرى.',
  },
  nav: {
    groupPurchases: 'المشتريات',
    suppliers: 'الموردون',
    purchaseInvoices: 'فواتير الشراء',
    receiveGoods: 'استلام بضاعة',
    receiptExcess: 'فروقات الاستلام',
    menu: 'القائمة',
    groupOverview: 'نظرة عامة',
    groupPeople: 'الموظفون والصلاحيات',
    groupOrganization: 'المؤسسة',
    groupControl: 'الرقابة',
    groupFinance: 'المالية',
    groupWarehouse: 'المخزن',
    dashboard: 'الرئيسية',
    inbox: 'الإشعارات',
    users: 'المستخدمون',
    roles: 'الأدوار والصلاحيات',
    locations: 'الفروع والمواقع',
    devices: 'الأجهزة',
    settings: 'الإعدادات',
    audit: 'سجل التدقيق',
    emergency: 'وضع الطوارئ',
    approvals: 'الموافقات',
    syncIssues: 'مشاكل المزامنة',
    messages: 'سجل الرسائل',
    health: 'حالة النظام والنسخ الاحتياطي',
    ledger: 'الدفاتر المحاسبية',
    account: 'حسابي',
    items: 'المواد',
    itemCategories: 'أصناف المواد',
    waste: 'الهدر والتالف',
    openings: 'الرصيد الافتتاحي',
    stockMatch: 'مطابقة المخزون مع الدفاتر',
    stockHere: 'المخزون حسب الموقع',
  },
  location: {
    switcher: 'الموقع الحالي',
    switcherHint: 'الصلاحيات والبيانات تُعرض حسب الموقع المختار',
    allLocations: 'كل المواقع',
    typeBRANCH: 'فرع',
    typeWAREHOUSE: 'مخزن',
    typeHEAD_OFFICE: 'الإدارة',
    ownershipOWNED: 'مملوك للشركة',
    ownershipFRANCHISE: 'امتياز (فرنشايز)',
    ownershipPARTNERSHIP: 'شراكة',
    royalty: 'نسبة الامتياز: {{rate}} من المبيعات',
    active: 'فعّال',
    inactive: 'غير مفعّل بعد',
    code: 'الرمز',
    city: 'المدينة',
    address: 'العنوان',
    phones: 'الهاتف',
    type: 'النوع',
    ownership: 'الملكية',
    // M8-5 (O39): what the website and the customer app show.
    publicInfo: 'المعلومات العامة',
    publicInfoTitle: 'المعلومات العامة — {{name}}',
    publicInfoIntro:
      'هذه المعلومات تظهر في الموقع وتطبيق الزبائن. الفرع يظهر فقط إذا كان فعّالاً ومعلَّماً «يظهر للعامة». الحقل الفارغ لا يظهر أبداً.',
    nameAr: 'الاسم بالعربية',
    nameEn: 'الاسم بالإنجليزية',
    addressAr: 'العنوان بالعربية',
    addressEn: 'العنوان بالإنجليزية',
    addressHint: 'اتركه فارغاً إذا لم يتأكد العنوان بعد؛ لا يظهر شيء مكانه.',
    publicPhones: 'أرقام الهاتف العامة',
    // The example is isolated left to right, or its digit groups show in reverse order.
    publicPhonesHint: 'رقم في كل سطر (حتى 3)، مثل ⁦0772 835 0100⁩.',
    hoursGiven: 'ساعات العمل معروفة',
    hoursHint:
      'بتوقيت بغداد. إذا كان الإغلاق قبل الفتح فالمقصود بعد منتصف الليل (مثلاً 12:00 إلى 02:00).',
    day: 'اليوم',
    opens: 'يفتح',
    closes: 'يغلق',
    closedDay: 'مغلق',
    // Owner, 2026-10-04: open 24 hours, the same hours for every day, times in the 24-hour form.
    dayMode: 'الحالة',
    openTimes: 'يفتح ويغلق',
    open24: 'مفتوح 24 ساعة',
    timeHint: 'بنظام 24 ساعة، مثل \u206609:30\u2069 أو \u206623:00\u2069.',
    badTime: 'اكتب الوقت بنظام 24 ساعة، مثل \u206609:30\u2069 أو \u206614:00\u2069.',
    sameForAll: 'نفس الساعات لكل الأيام',
    sameForAllHint: 'ينسخ ساعات السبت إلى باقي الأيام.',
    warnTitle: 'تنبيه: ساعات غير معتادة',
    warnIntro: 'تأكد من هذه الأيام قبل الحفظ:',
    warnShort: '{{day}}: مفتوح أقل من ساعة (\u2066{{open}} – {{close}}\u2069).',
    warnNight: '{{day}}: يفتح بعد منتصف الليل وقبل الخامسة صباحاً (\u2066{{open}}\u2069).',
    warnAlmostAllDay: '{{day}}: مفتوح 20 ساعة أو أكثر. هل تقصد «مفتوح 24 ساعة»؟',
    saveAnyway: 'حفظ على أي حال',
    backToEdit: 'رجوع للتعديل',
    isPublic: 'يظهر للعامة (الموقع وتطبيق الزبائن)',
    isPublicHint: 'للفروع فقط. الفرع غير الفعّال لا يظهر حتى لو عُلِّم.',
    isPublicNotBranch: 'المخزن والإدارة لا يظهران للعامة.',
    shownPublicly: 'يظهر للعامة',
    notShownPublicly: 'لا يظهر للعامة',
    saved: 'حُفظت المعلومات العامة.',
  },
  /** Days of the week, Saturday first (the Iraqi week). */
  week: {
    sat: 'السبت',
    sun: 'الأحد',
    mon: 'الاثنين',
    tue: 'الثلاثاء',
    wed: 'الأربعاء',
    thu: 'الخميس',
    fri: 'الجمعة',
  },
  /** The public website (M8-5): home and branches. */
  site: {
    brand: 'دوب بركر',
    metaDescription:
      'دوب بركر — فروعنا وأرقام الاتصال وساعات العمل. الطلب من الموقع والتطبيق قريباً.',
    home: 'الرئيسية',
    branches: 'الفروع',
    otherLanguage: 'English',
    heroTitle: 'أهلاً بك في دوب بركر',
    comingSoon: 'قريباً: اطلب من الموقع ومن تطبيق دوب بركر.',
    // The store names are isolated left to right, or «و» sticks to "App" and the words mix.
    appsSoon: 'تطبيق دوب بركر قريباً على ⁦Google Play⁩ و⁦App Store⁩.',
    seeBranches: 'فروعنا وأرقامها',
    branchesTitle: 'فروعنا',
    branchesIntro: 'اتصل بالفرع مباشرة.',
    noBranches: 'لا توجد فروع معروضة الآن.',
    unavailable: 'تعذّر عرض الفروع الآن. حاول بعد قليل.',
    call: 'اتصال',
    hours: 'ساعات العمل',
    closed: 'مغلق',
    allDay: 'مفتوح 24 ساعة',
    am: 'ص',
    pm: 'م',
    staging: 'نسخة تجربة من الموقع — ليست للاستخدام العام.',
    rights: '© دوب بركر',
  },
  /** The customer app "Dob Burger" (M8-5); the rest of its words are the site's. */
  customer: {
    comingSoon: 'قريباً: اطلب من تطبيق دوب بركر.',
    loading: 'جارٍ تحميل الفروع…',
    savedList: 'بدون إنترنت — هذه آخر قائمة محفوظة.',
    retry: 'حاول مرة أخرى',
  },
  dashboard: {
    welcome: 'أهلاً {{name}}',
    intro: 'هذه لوحة إدارة دوب بركر. ما تراه هنا يعتمد على صلاحياتك.',
    systemStatus: 'حالة النظام',
    database: 'قاعدة البيانات',
    queue: 'طابور الرسائل',
    storage: 'تخزين الملفات',
    backups: 'النسخ الاحتياطي',
    ok: 'يعمل',
    down: 'متوقف',
    version: 'الإصدار: {{commit}}',
    myRoles: 'أدواري',
    unread: 'إشعارات غير مقروءة',
    pendingApprovals: 'موافقات بانتظار القرار',
    openSyncIssues: 'مشاكل مزامنة مفتوحة',
    open: 'فتح',
    whatsNext: 'الشاشات القادمة',
    whatsNextBody:
      'هذه النسخة الأولى من لوحة الإدارة. الشاشات المعلّمة بـ«قريباً» تُبنى في الأجزاء القادمة بعد ملاحظاتك.',
  },
  users: {
    phoneHint: 'موبايل عراقي: 11 رقماً يبدأ بـ 07. رقم أجنبي: اكتب + ثم رمز الدولة.',
    title: 'المستخدمون',
    subtitle: 'كل من يستخدم النظام: الشركاء والإدارة وموظفو الفروع.',
    searchPlaceholder: 'ابحث بالاسم أو رقم الهاتف',
    newUser: 'مستخدم جديد',
    create: 'إنشاء المستخدم',
    created: 'تم إنشاء المستخدم',
    tempPasswordTitle: 'كلمة المرور المؤقتة',
    tempPasswordIntro:
      'أعطِ هذه الكلمة للموظف شخصياً. ستظهر مرة واحدة فقط، وسيُطلب منه تغييرها عند أول دخول.',
    preferredLang: 'لغة الموظف',
    statusACTIVE: 'فعّال',
    statusSUSPENDED: 'موقوف',
    statusLEFT: 'ترك العمل',
    twoFactor: 'التحقق بخطوتين',
    twoFactorOn: 'مفعّل',
    twoFactorOff: 'غير مفعّل',
    pin: 'رمز PIN للكاشير',
    pinSet: 'موجود',
    pinNotSet: 'غير موجود',
    roles: 'الأدوار',
    noRoles: 'لا توجد أدوار حالية.',
    roleScope: 'النطاق',
    since: 'منذ',
    until: 'حتى',
    ended: 'منتهي',
    assignRole: 'إضافة دور',
    role: 'الدور',
    roleNeeds2fa: 'هذا الدور من أدوار الإدارة ويتطلب التحقق بخطوتين.',
    scopeLocationRequired: 'هذا الدور يُعطى لموقع محدد. اختر الموقع.',
    endRole: 'إنهاء الدور',
    endRoleTitle: 'إنهاء دور «{{role}}»',
    suspend: 'إيقاف الحساب',
    suspendTitle: 'إيقاف حساب {{name}}',
    suspendIntro: 'يُسجَّل خروجه من كل الأجهزة فوراً ولا يستطيع الدخول حتى يُعاد تفعيله.',
    reactivate: 'إعادة التفعيل',
    reactivateTitle: 'إعادة تفعيل حساب {{name}}',
    resetTitle: 'إعادة تعيين',
    resetPassword: 'إعادة تعيين كلمة المرور',
    resetTwoFactor: 'إعادة تعيين التحقق بخطوتين',
    resetPin: 'مسح رمز PIN',
    resetIntro: 'تُسجَّل هذه العملية في سجل التدقيق. إعادة تعيين حساب إداري تُبلغ جميع الشركاء.',
    resetDone: 'تمت إعادة التعيين.',
    notAllowedHint: 'لا يمكنك التصرف بحساب يملك صلاحيات أعلى منك، ولا بحسابك أنت.',
    back: 'كل المستخدمين',
    editDetails: 'تعديل البيانات',
    editTitle: 'تعديل بيانات {{name}}',
    editIntro:
      'يُحفظ التعديل في سجل التدقيق (القيمة القديمة والجديدة والسبب). تغيير رقم الهاتف أو البريد لحساب إداري يُبلغ جميع الشركاء فوراً.',
    editNothingChanged: 'لم تغيّر شيئاً بعد.',
    editSaved: 'تم حفظ التعديل.',
    phoneBreaksRule: 'هذا الرقم المحفوظ لا يطابق قاعدة أرقام الهاتف. صحّحه من «تعديل البيانات».',
    emailClearHint: 'اتركه فارغاً لحذف البريد.',
    endsOn: 'ينتهي الدور في (اختياري)',
    endsOnHint:
      'لعمال اليومية والموسم: يتوقف الدخول وحده. يبقى الدور حتى نهاية يوم العمل، أي صباح اليوم التالي، فلا تُقطع وجبة ليلية.',
    endsAtPreview: 'ينتهي الدور: {{when}}',
    endsAtFull: '{{date}}، الساعة {{time}} بتوقيت بغداد',
    endsAtLoading: 'يُحسب وقت الانتهاء…',
    endsAtFailed: 'تعذّر حساب وقت الانتهاء. تحقّق من التاريخ.',
    endsOnLabel: 'ينتهي في {{when}}',
    renew: 'تجديد',
    setEnd: 'تحديد تاريخ انتهاء',
    renewTitle: 'تجديد دور «{{role}}»',
    setEndTitle: 'تحديد تاريخ انتهاء لدور «{{role}}»',
    renewIntro:
      'اختر التاريخ الجديد. يظهر وقت الانتهاء بالضبط قبل الحفظ، ويُحفظ السبب في سجل التدقيق.',
    currentEnd: 'ينتهي حالياً: {{when}}',
    newEndsOn: 'التاريخ الجديد',
    roleEndsSoon: 'دور ينتهي {{when}}',
  },
  devices: {
    title: 'الأجهزة',
    subtitle:
      'أجهزة الكمبيوتر المسجّلة في الفروع والمخزن والإدارة. كل جهاز يُسجَّل برمز لمرة واحدة، ويمكن إلغاؤه في أي وقت.',
    newCode: 'رمز تسجيل جهاز جديد',
    empty: 'لا توجد أجهزة مسجّلة في هذا الموقع بعد.',
    name: 'اسم الجهاز',
    type: 'النوع',
    typeHUB: 'الجهاز الرئيسي للموقع (Hub)',
    typeDESKTOP: 'كمبيوتر (كاشير أو مكتب)',
    typeKIOSK: 'شاشة طلب ذاتي',
    typePHONE: 'هاتف',
    typeBACKUP_AGENT: 'حافظ النسخ الاحتياطي',
    typeHintHUB: 'جهاز واحد مخصص في كل موقع، على UPS. باقي الأجهزة تتصل به عبر شبكة الفرع.',
    typeHintDESKTOP: 'كمبيوتر كاشير أو مكتب يعمل عبر الجهاز الرئيسي للموقع.',
    typeHintKIOSK: 'شاشة يطلب منها الزبائن بأنفسهم داخل المطعم.',
    typeHintBACKUP_AGENT: 'كمبيوتر المكتب الذي يسحب نسخة احتياطية مشفّرة كل ليلة.',
    location: 'الموقع',
    status: 'الحالة',
    statusACTIVE: 'فعّال',
    statusREVOKED: 'ملغى',
    appVersion: 'الإصدار',
    lastSeen: 'آخر اتصال',
    never: 'لم يتصل بعد',
    enrolledAt: 'تاريخ التسجيل',
    revoke: 'إلغاء الجهاز',
    revokeTitle: 'إلغاء الجهاز «{{name}}»',
    revokeIntro:
      'الإلغاء نهائي: يتوقف الجهاز فوراً عن المزامنة وتنتهي كل الجلسات المفتوحة عليه. لإعادته يجب تسجيله من جديد.',
    revokedBecause: 'السبب: {{reason}}',
    hubCertificate: 'بصمة شهادة الشبكة الداخلية',
    hubCertificateMissing: 'لم يسجّل الجهاز الرئيسي بصمة شهادته بعد (يسجّلها عند اتصاله بالإنترنت)',
    connection: 'طريقة الاتصال / قاعدة الهاتف',
    connectionHub: 'عبر الجهاز الرئيسي {{address}}',
    connectionServer: 'مباشرة إلى الخادم',
    connectionNone: 'بلا اتصال',
    connectionAt: 'آخر تقرير {{time}}',
    storeENCRYPTED: 'قاعدته مشفّرة',
    storeMOVE_FAILED: 'لم يُنقل إلى القاعدة المشفّرة — يرسل من القديمة',
    storeKEY_LOST: 'مفتاح قاعدته مفقود',
    storeSTORE_UNREADABLE: 'قاعدته لا تُفتح',
    storeFOLDER_UNREADABLE: 'تعذّرت قراءة مجلد بياناته',
    storeDetail: 'السبب: {{detail}}',
    photosWaiting: 'صور لم تصل بعد: {{count}}',
    photosProblem: 'الصور لا تُرسل منذ {{time}}',
    photosStep_link: 'عند طلب رابط الرفع من الخادم',
    photosStep_upload: 'عند رفع الصورة إلى التخزين',
    photosStep_confirm: 'عند طلب تأكيد الخادم',
    codeTitle: 'رمز التسجيل',
    codeIntro:
      'اكتب هذا الرمز في شاشة الإعداد الأولى لبرنامج Dob Desktop على الجهاز الجديد. يعمل مرة واحدة فقط، ولن يظهر مرة أخرى.',
    codeExpires: 'صالح حتى {{time}} (30 دقيقة).',
    create: 'إنشاء الرمز',
  },
  printers: {
    title: 'الطابعات',
    titleAt: 'الطابعات — {{location}}',
    subtitle:
      'طابعات الشبكة الحرارية في هذا الموقع (ESC/POS، المنفذ 9100). كل تغيير يُحفظ في سجل التدقيق.',
    open: 'الطابعات',
    back: 'العودة إلى المواقع',
    add: 'إضافة طابعة',
    edit: 'تعديل',
    editTitle: 'تعديل «{{name}}»',
    remove: 'إزالة من الاستعمال',
    removeTitle: 'إزالة «{{name}}» من الاستعمال',
    removeIntro:
      'تختفي الطابعة من الاستعمال ولا يمكن إرجاعها، لكنها تبقى في السجل. لاستعمالها مرة أخرى أضفها من جديد.',
    history: 'السجل',
    name: 'الاسم',
    nameHint: 'اسم يعرفه الموظفون، مثل «كاشير 1» أو «المطبخ».',
    host: 'عنوان IP',
    hostHint:
      'كما يظهر في صفحة الفحص الذاتي التي تطبعها الطابعة، مثل 192.168.1.50. اطلب لها عنواناً ثابتاً في الراوتر.',
    port: 'المنفذ',
    portHint: '9100 لكل طابعات الشبكة تقريباً.',
    address: 'العنوان',
    paper: 'عرض الورق',
    paper80: '80 مم',
    paper58: '58 مم',
    paperHint: 'عرض لفّة الورق. 80 مم ما لم تكن الطابعة غير ذلك.',
    use: 'الاستعمال',
    roleRECEIPT: 'الفواتير',
    roleKITCHEN: 'المطبخ',
    roleOTHER: 'أخرى',
    inUse: 'مستعملة',
    retired: 'أُزيلت',
    retiredOn: 'أُزيلت {{time}}: {{reason}}',
    pending: 'بانتظار الخادم',
    pendingHint: 'تغيّرت في هذا الموقع بدون إنترنت، والخادم يتحقق منها عند وصولها.',
    empty: 'لا توجد طابعات في هذا الموقع بعد.',
    showRetired: 'الطابعات المُزالة ({{count}})',
    saved: 'تم الحفظ.',
    cannotManage:
      'يمكنك رؤية الطابعات هنا، لكن إضافتها أو تعديلها يحتاج صلاحية «إضافة وتعديل وحذف طابعات الموقع».',
    testPrint: 'طباعة تجريبية',
    testPrintWeb:
      'الطباعة التجريبية تُرسل من برنامج Dob Desktop في هذا الموقع، لأن الطابعة على شبكة الفرع ولا يمكن الوصول إليها من الإنترنت.',
    desktopSubtitle:
      'طابعات الشبكة في هذا الموقع. يمكن لأي شخص رؤيتها وطباعة صفحة تجريبية، أما التعديل فيحتاج مدير الفرع أو تقنية المعلومات، ويعمل أيضاً بدون إنترنت (يتحقق منه الخادم عند الاتصال).',
    accessTitle: 'تعديل الطابعات يحتاج تسجيل دخول',
    accessNeedSignIn:
      'إضافة طابعة أو تعديلها أو إزالتها تحتاج أن يكون مدير هذا الفرع أو تقنية المعلومات داخلاً على هذا الجهاز (بإنترنت أو بدونه).',
    accessNotAllowed:
      'لا يُسمح لـ {{name}} بتعديل الطابعات. يلزم مدير هذا الفرع أو تقنية المعلومات.',
    accessServerUnreachable: 'لا يمكن التأكد من الدخول الحالي بدون الخادم. ادخل بدون إنترنت.',
    accessBy: 'يمكن تعديل الطابعات الآن: {{name}} ({{session}})',
    signIn: 'تسجيل الدخول',
    routeself: 'الطباعة التجريبية من هذا الجهاز يطبعها هذا الجهاز (الجهاز الرئيسي).',
    routehub: 'الطباعة التجريبية من هذا الجهاز تُرسل إلى الجهاز الرئيسي وهو يطبعها.',
    routealone: 'الجهاز الرئيسي لا يستجيب: هذا الجهاز يطبع بنفسه.',
    testPrintSent: 'أُرسلت الصفحة التجريبية إلى «{{name}}».',
    jobs: 'آخر أوامر الطباعة',
    jobsEmpty: 'لا توجد أوامر طباعة بعد.',
    jobWhen: 'الوقت',
    jobState: 'الحالة',
    jobWhere: 'الطابور',
    wherehere: 'هذا الجهاز',
    wherehub: 'الجهاز الرئيسي',
    statequeued: 'بالانتظار',
    stateprinting: 'تُطبع الآن',
    statedone: 'طُبعت',
    statefailed: 'فشلت',
    statecancelled: 'أُلغيت',
    attempts: 'المحاولات: {{count}}',
    errUNREACHABLE: 'الطابعة لا تستجيب على عنوانها (مطفأة، أو السلك مفصول، أو تغيّر عنوانها).',
    errCONNECTION_LOST: 'قطعت الطابعة الاتصال أثناء الطباعة.',
    errTIMEOUT: 'توقفت الطابعة عن استلام البيانات.',
    errPRINTER_REMOVED: 'هذه الطابعة أُزيلت من الاستعمال.',
    errRENDER_FAILED: 'تعذّر تجهيز الصفحة للطباعة.',
    errINVALID_DOCUMENT: 'الصفحة غير صالحة.',
    retryJob: 'أعد المحاولة',
    cancelJob: 'إلغاء',
    errSIGN_IN_REQUIRED: 'سجّل الدخول باسم مدير هذا الفرع أو تقنية المعلومات أولاً.',
    errNOT_ALLOWED: 'هذا الحساب لا يُسمح له بتعديل الطابعات.',
    errSERVER_UNREACHABLE: 'لا يمكن التأكد من الدخول بدون الخادم. ادخل بدون إنترنت.',
    errPRINTER_NOT_FOUND: 'هذه الطابعة ليست في قائمة هذا الجهاز.',
    errNOT_REGISTERED: 'هذا الجهاز غير مسجّل بعد.',
    notAvailable: 'تظهر الطابعات بعد تسجيل هذا الجهاز في موقع.',
  },
  messages: {
    title: 'سجل الرسائل',
    subtitle: 'كل رسالة واتساب وبريد وإشعار أرسلها النظام، مع حالتها وعدد المحاولات.',
    channels: 'قنوات الإرسال',
    channelWHATSAPP: 'واتساب',
    channelEMAIL: 'البريد الإلكتروني',
    channelPUSH: 'إشعارات الهاتف',
    transport: 'طريقة الإرسال: {{kind}}',
    transportStub: 'تجريبي — لا يُرسل شيء فعلاً',
    transportStubHint: 'تُفعّل عند إضافة بيانات الخدمة في إعدادات الخادم.',
    transportLive: 'مُفعّل',
    sendTest: 'أرسل رسالة تجريبية لي',
    testQueued: 'أُضيفت رسالة تجريبية. حالتها: {{status}}.',
    testNoAddress: 'لم تُضف أي رسالة: لا يوجد لحسابك عنوان لهذه القناة (بريد أو تطبيق هاتف).',
    filterStatus: 'الحالة',
    filterChannel: 'القناة',
    filterTemplate: 'نوع الرسالة',
    empty: 'لا توجد رسائل بهذه الشروط.',
    time: 'الوقت',
    recipient: 'المستلم',
    channel: 'القناة',
    template: 'نوع الرسالة',
    status: 'الحالة',
    attempts: 'المحاولات',
    details: 'التفاصيل',
    lastError: 'آخر خطأ',
    params: 'المحتوى',
    statusQUEUED: 'في الانتظار',
    statusSENT: 'أُرسلت',
    statusDELIVERED: 'وصلت',
    statusFAILED: 'فشلت',
    statusSTUBBED: 'تجريبي — لم تُرسل',
    statusSKIPPED: 'تُخطّيت',
    templatepassword_reset_code: 'رمز استعادة كلمة المرور',
    templatepartner_alert: 'تنبيه للشركاء',
    templatetest_message: 'رسالة تجريبية',
    showing: 'آخر {{count}} رسالة',
  },
  health: {
    title: 'حالة النظام والنسخ الاحتياطي',
    subtitle: 'فحص مباشر للخادم كل 30 ثانية.',
    overall: 'الحالة العامة',
    allOk: 'كل شيء يعمل',
    degraded: 'يوجد عطل',
    unreachable: 'الخادم لا يستجيب',
    services: 'الخدمات',
    uptime: 'مدة التشغيل',
    version: 'الإصدار (git)',
    checkedAt: 'آخر فحص',
    refresh: 'افحص الآن',
    backups: 'النسخ الاحتياطي',
    backupsNotConfigured: 'غير مُفعّل على هذا الخادم',
    backupsOk: 'سليم',
    backupsWarning: 'يحتاج انتباه',
    backupsFailed: 'توجد مشكلة',
    backupsSchedule:
      'نسخة مشفّرة كل ليلة الساعة 4:30 فجراً على الخادم وعلى Google Drive، واختبار استرجاع تلقائي في اليوم الأول من كل شهر. عند أي مشكلة يصل تنبيه للشركاء ولقسم تقنية المعلومات.',
    lastBackup: 'آخر نسخة احتياطية',
    lastRestoreTest: 'آخر اختبار استرجاع',
    officeCopy: 'نسخة كمبيوتر المكتب',
    runOk: 'ناجح',
    runFailed: 'فشل',
    failedStep: 'فشل في خطوة: {{step}}',
    never: 'لم يعمل بعد',
    unreadable: 'غير مقروء',
    googleDrive: 'Google Drive',
    driveok: 'النسخة محدّثة',
    drivefailed: 'فشل الإرسال',
    drivenot_configured: 'غير مربوط',
    contents: 'المحتوى',
    contentsValue: '{{tables}} جدول، {{rows}} سجل',
    snapshots: 'النسخ المحفوظة',
    problems: 'ما يحتاج إصلاح',
    problembackup_missing: 'لم تُؤخذ أي نسخة احتياطية بعد.',
    problembackup_unreadable: 'ملف حالة النسخ الاحتياطي غير مقروء.',
    problembackup_failed: 'فشلت آخر نسخة احتياطية.',
    problembackup_late: 'لم تُؤخذ نسخة احتياطية منذ أكثر من {{hours}} ساعة.',
    problemdrive_failed: 'فشل إرسال آخر نسخة إلى Google Drive.',
    problemdrive_not_configured: 'Google Drive غير مربوط: النسخة موجودة على الخادم فقط.',
    problemrestore_test_missing: 'لم يُجرَ اختبار استرجاع بعد.',
    problemrestore_test_unreadable: 'ملف حالة اختبار الاسترجاع غير مقروء.',
    problemrestore_test_failed: 'فشل آخر اختبار استرجاع.',
    problemrestore_test_late: 'لم يُجرَ اختبار استرجاع منذ أكثر من {{days}} يوماً.',
    problemoffice_copy_never: 'جهاز المكتب مسجّل لكنه لم يرسل أي تقرير نسخ بعد.',
    problemoffice_copy_failed:
      'فشلت آخر محاولة سحب على جهاز المكتب (نسخ السيرفر و Google Drive سليمة).',
    problemoffice_copy_late:
      'أحدث نسخة على جهاز المكتب أقدم من آخر نسخة على السيرفر بأكثر من {{hours}} ساعة.',
    problemoffice_incident_mass_deletion:
      'اختفى جزء كبير من النسخ من السيرفر دفعة واحدة. جهاز المكتب يحتفظ بكل شيء ولا يحذف شيئاً حتى يسمح أحد الشركاء.',
    problemoffice_incident_config_replaced:
      'ملف إعداد المستودع على السيرفر ليس نفس الملف الذي عند جهاز المكتب؛ احتفظ الجهاز بملفه.',
    problemoffice_incident_file_replaced:
      'السيرفر يعرض ملف نسخة بمحتوى مختلف عن الملف الذي تحقّق منه جهاز المكتب؛ احتفظ الجهاز بملفه.',
    problemoffice_incident_hash_mismatch: 'ملف وصل من السيرفر لا تطابق بصمته، فرُفض.',
    problemoffice_incident_key_removed:
      'حُذف ملف مفتاح من المستودع على السيرفر؛ جهاز المكتب يحتفظ بنسخته.',
    uptimeValue: '{{days}} يوم {{hours}} ساعة {{minutes}} دقيقة',
    storageTitle: 'القرص والصور',
    diskOk: 'المساحة كافية',
    diskLow: 'المساحة تقترب من النفاد',
    diskNotMeasured: 'لا تُقاس على هذا الخادم',
    diskFree: 'الفارغ على الخادم',
    diskFreeValue: '{{free}} من {{total}}',
    photosStored: 'الصور المحفوظة',
    photosStoredValue: '{{photos}} صورة، {{size}}',
    photosWaiting: 'صور لم تصل بعد',
    storageHint:
      'قاعدة البيانات والصور والنسخ الاحتياطية الليلية تتشارك قرص الخادم. إذا نزل الفارغ عن 20 GB (أو عُشر القرص) يجب أن يتصرف قسم IT قبل أن تتوقف النسخ الاحتياطية.',
  },
  backupAgent: {
    title: 'نسخة المكتب الاحتياطية',
    subtitle:
      'هذا الجهاز يحفظ النسخة الثالثة من النسخ الاحتياطية. يسحب النسخة المشفّرة من السيرفر كل يوم، ولا يملك مفتاحها أبداً.',
    updates: 'التحديثات',
    notAgent: 'هذا الجهاز ليس جهاز النسخ الاحتياطي للمكتب.',
    error_NOT_ABSOLUTE: 'اختر مجلداً على قرص في هذا الجهاز (ليس مجلد شبكة).',
    error_NOT_A_LOCAL_DISK:
      'هذا المجلد ليس على قرص داخلي في هذا الجهاز. لا تُستخدم أقراص الشبكة ولا الفلاشات: يجب أن يعمل النسخ حتى لو لم يدخل أحد إلى الجهاز.',
    error_NOT_WRITABLE: 'ويندوز لا يسمح لهذا الحساب بالكتابة في هذا المجلد. اختر مجلداً آخر.',
    error_NOT_ENOUGH_SPACE:
      'المساحة الفارغة على هذا القرص غير كافية: يجب أن تبقى 10 GB فارغة على الأقل.',
    error_COPY_RUNNING: 'يوجد نسخ جارٍ الآن. انتظر حتى ينتهي.',
    error_NO_FOLDER: 'اختر المجلد أولاً.',
    error_NOT_BACKUP_AGENT: 'هذا الجهاز ليس جهاز النسخ الاحتياطي للمكتب.',
    error_DEVELOPMENT_BUILD: 'نسخة التطوير لا تستطيع إنشاء مهمة ويندوز.',
    error_TASK_FAILED: 'لم تُنشئ ويندوز المهمة. حاول مرة أخرى أو اسأل قسم تقنية المعلومات.',
    error_other: 'لم ينجح ({{code}}).',
    folderTitle: 'أين تُحفظ النسخة؟',
    folderIntro:
      'اختر قرصاً في هذا الجهاز فيه مساحة فارغة كبيرة. تبقى {{min}} فارغة على الأقل دائماً، والنسخة تكبر قليلاً كل يوم.',
    driveFree: '{{free}} فارغة من {{total}}',
    enough: 'مساحة كافية',
    notEnough: 'أقل من {{min}} فارغة',
    noDrives: 'لم يُعثر على قرص داخلي.',
    useSuggested: 'استخدم',
    pickFolder: 'اختر مجلداً آخر…',
    folderHint:
      'النسخة تُحفظ داخل المجلد في مجلد «staging» أو «production». تغيير المجلد لاحقاً يبدأ نسخة جديدة هناك (القديمة تبقى).',
    phase_manifest: 'يسأل السيرفر عن الجديد…',
    phase_checking: 'يفحص الملفات الموجودة على هذا الجهاز…',
    phase_downloading: 'ينسخ…',
    phase_cleaning: 'يحذف الملفات التي حذفها السيرفر قبل أكثر من 30 يوماً…',
    phase_reporting: 'يُبلغ السيرفر بالنتيجة…',
    progress: '{{done}} من {{total}} ({{files}} من {{filesTotal}} ملف)',
    lastFailed: 'توقّف آخر نسخ عند: {{step}}.',
    step_manifest: 'سؤال السيرفر (لا يوجد إنترنت؟)',
    step_disk: 'مساحة القرص',
    step_download: 'التنزيل (يكمل من حيث توقّف في المرة القادمة)',
    step_verify: 'فحص البصمة',
    step_write: 'الكتابة على القرص',
    step_cleanup: 'التنظيف',
    retryHint: 'يحاول مرة أخرى وحده بعد 30 دقيقة.',
    lastOk: 'آخر نسخ: {{time}}.',
    incidentsTitle: 'النسخة رفضت شيئاً بنفسها',
    incident_MASS_DELETION:
      'اختفى جزء كبير من النسخ الاحتياطية ({{files}} ملف، {{size}}) من السيرفر دفعة واحدة. هذا الجهاز يحتفظ بكل شيء.',
    incident_CONFIG_REPLACED:
      'تغيّر ملف إعداد النسخ الاحتياطية على السيرفر. هذا الجهاز احتفظ بملفه.',
    incident_FILE_REPLACED:
      'السيرفر يعرض ملفاً بمحتوى مختلف عن الذي فحصه هذا الجهاز. هذا الجهاز احتفظ بملفه.',
    incident_HASH_MISMATCH: 'ملف وصل من السيرفر لم تطابق بصمته، فرُفض.',
    incident_KEY_REMOVED: 'حُذف ملف مفتاح على السيرفر. هذا الجهاز يحتفظ بنسخته.',
    incidentsHint:
      'وصل تنبيه للشركاء ولقسم تقنية المعلومات. القرار لأحد الشركاء من لوحة الإدارة ← صحة النظام؛ لا يُغيَّر شيء من هنا.',
    folder: 'المجلد',
    lastSuccess: 'آخر نسخ ناجح',
    never: 'لم يحدث بعد',
    nextRun: 'النسخ القادم',
    copyingNow: 'ينسخ الآن',
    scheduleHint: 'كل يوم الساعة {{hour}}:00، وفور تشغيل الجهاز إذا كان مطفأً وقتها.',
    newest: 'أحدث نسخة على هذا الجهاز',
    size: 'النسخة',
    sizeValue: '{{size}} ({{files}} ملف، {{snapshots}} نسخة)',
    free: 'المساحة الفارغة',
    kept: 'حُذف من السيرفر ومحفوظ هنا',
    keptNone: 'لا شيء',
    keptValue: '{{files}} ملف، {{size}} (يُحفظ 30 يوماً)',
    pending: 'تقارير بانتظار السيرفر',
    copyNow: 'انسخ الآن',
    changeFolder: 'غيّر المجلد…',
    changeFolderHint: 'المجلد الجديد يبدأ نسخة كاملة جديدة.',
    taskTitle: 'النسخ حتى لو لم يدخل أحد إلى الجهاز',
    task_always: 'ينسخ حتى لو لم يدخل أحد',
    task_missing: 'لم تُجهَّز بعد',
    'task_signed-in-only': 'ينسخ فقط عندما يكون أحد داخلاً إلى الجهاز',
    'task_without-password': 'مجهّزة بدون كلمة المرور',
    task_unknown: 'غير مقروءة',
    'task_not-windows': 'غير موجودة على هذا الجهاز',
    task_password: 'تغيّرت كلمة مرور ويندوز',
    taskHelp_always: 'ويندوز تبدأ النسخ كل ساعة وعند الدخول، والبرنامج ينسخ فقط عندما يحين وقته.',
    taskHelp_missing:
      'أنشئ مهمة ويندوز: تبدأ النسخ كل ساعة وعند الدخول (ينسخ فقط عندما يحين وقته). بعدها حوّلها إلى «التشغيل سواء كان المستخدم داخلاً أم لا» (docs/BACKUP_AGENT_AR.md).',
    'taskHelp_signed-in-only':
      'الآن ينسخ فقط عندما يكون أحد داخلاً إلى هذا الجهاز. لينسخ الساعة 06:00 بدون أن يدخل أحد: افتح جدولة المهام ← «Dob Backup Agent» ← خصائص ← «التشغيل سواء كان المستخدم داخلاً أم لا» ← موافق، واكتب كلمة مرور حساب ويندوز هذا في نافذة ويندوز نفسها (docs/BACKUP_AGENT_AR.md).',
    'taskHelp_without-password':
      'خيار «عدم حفظ كلمة المرور» مفعّل: ويندوز عندها لا تسمح لبرنامج Dob Desktop بفتح ملفاته المحمية، فلا يستطيع النسخ. ألغِ هذا الخيار في جدولة المهام واكتب كلمة المرور.',
    taskHelp_unknown: 'لم تُخبرنا ويندوز كيف جُهّزت المهمة. افتح جدولة المهام للتحقق.',
    'taskHelp_not-windows': 'مهمة ويندوز موجودة فقط في البرنامج المثبّت على ويندوز.',
    taskHelp_password:
      'لم تستطع ويندوز بدء النسخ: تغيّرت كلمة مرور الحساب. في جدولة المهام ← «Dob Backup Agent» ← خصائص ← موافق، اكتب كلمة المرور الجديدة.',
    taskLastRun: 'آخر مرة شغّلتها ويندوز: {{time}}',
    createTask: 'أنشئ مهمة ويندوز',
    openTaskScheduler: 'افتح جدولة المهام',
  },
  office: {
    title: 'نسخة المكتب (النسخة الاحتياطية الثالثة)',
    notAvailable: 'غير متاحة على هذا الخادم',
    noAgent: 'لا يوجد جهاز مكتب بعد',
    noAgentHelp:
      'يسجّل قسم تقنية المعلومات جهاز المكتب من صفحة الأجهزة برمز «Backup Agent». بعدها يسحب الجهاز النسخة المشفّرة كل يوم، ولا يملك مفتاح فكّ التشفير أبداً.',
    pc: 'جهاز المكتب',
    lastSeen: '(آخر اتصال {{time}})',
    lastRun: 'آخر تشغيل',
    error: 'ما الخطأ',
    lastSuccess: 'آخر نسخ ناجح',
    newestHeld: 'أحدث نسخة على جهاز المكتب',
    serverBackup: 'آخر نسخة على السيرفر: {{time}}',
    size: 'على جهاز المكتب',
    sizeValue: '{{size}} ({{files}} ملف، {{snapshots}} نسخة)',
    free: 'المساحة الفارغة على الجهاز',
    kept: 'حُذف من السيرفر ومحفوظ هنا',
    keptNone: 'لا شيء',
    keptValue: '{{files}} ملف، {{size}} (يُحفظ {{days}} يوماً)',
    incident: 'رفضه الجهاز بنفسه',
    detected: 'متى',
    concerns: 'يخص',
    concernsValue: '{{files}} ملف، {{size}}',
    state: 'الحالة',
    closed: 'انتهى',
    open: 'ما زال مرفوضاً',
    partnerOnly: 'بانتظار أحد الشركاء',
    release: 'السماح',
    releasedBy: 'سمح به {{name}} في {{time}}',
    releaseTitle: 'السماح لنسخة المكتب بالمتابعة',
    releaseHint: '10 أحرف على الأقل: لماذا كان هذا متوقعاً. يُبلَّغ جميع الشركاء.',
    releaseIntroMASS_DELETION:
      'اختفى {{files}} ملف ({{size}}) من السيرفر دفعة واحدة، فاحتفظ بها جهاز المكتب. اسمح بذلك فقط إذا كنت تعرف سبب الحذف (مثلاً تنظيف مخطّط له). بعدها تُحذف من جهاز المكتب بعد أيام الانتظار العادية.',
    releaseIntroCONFIG_REPLACED:
      'تغيّر ملف إعداد المستودع على السيرفر، فاحتفظ جهاز المكتب بملفه القديم. اسمح بذلك فقط بعد ترقية مخطّط لها للمستودع. يبقى الملف القديم محفوظاً جانباً على جهاز المكتب.',
    kindMASS_DELETION: 'حذف جماعي على السيرفر',
    kindCONFIG_REPLACED: 'تغيّر إعداد المستودع',
    kindFILE_REPLACED: 'ملف استُبدل على السيرفر',
    kindHASH_MISMATCH: 'البصمة غير مطابقة',
    kindKEY_REMOVED: 'مفتاح حُذف على السيرفر',
    stepmanifest: 'قراءة القائمة',
    stepdisk: 'مساحة القرص',
    stepdownload: 'التنزيل',
    stepverify: 'فحص البصمة',
    stepwrite: 'الكتابة على القرص',
    stepcleanup: 'التنظيف',
    schedule:
      'يسحب جهاز المكتب كل يوم الساعة {{hour}}:00 (وعند التشغيل إذا كان متأخراً). ما يُحذف من السيرفر يُحفظ {{days}} يوماً، والحذف الجماعي المفاجئ لا يُتبع أبداً بدون موافقة أحد الشركاء.',
  },
  roles: {
    scopeALL: 'كل الشركة',
    scopeLOCATION: 'موقع محدد',
    title: 'الأدوار والصلاحيات',
    subtitle:
      'كل دور مجموعة صلاحيات. ما تغيّره هنا يسري فوراً على كل من يحمل الدور، ويُبلَّغ به جميع الشركاء.',
    tabRoles: 'الأدوار',
    tabMatrix: 'جدول الصلاحيات',
    members: '{{count}} شخص',
    permissionsCount: '{{count}} صلاحية',
    twoFactor: 'تحقق بخطوتين',
    open: 'فتح الدور',
    matrixHint: 'كل سطر صلاحية، وكل عمود دور. العلامة ✓ تعني أن الدور يملك الصلاحية.',
    has: 'يملك الصلاحية',
    back: 'كل الأدوار',
    permissionsTitle: 'صلاحيات هذا الدور',
    sensitive: 'حساسة',
    sensitiveHint: 'صلاحية حساسة: تعطي قدرة على أشخاص آخرين أو على المال أو الأمان.',
    offlineTitle: 'ما يُسمح به بدون إنترنت',
    offlineNone: 'لا شيء: هذا الدور لا يعمل بدون إنترنت.',
    offlineAt: 'على أجهزة {{type}}: {{list}}',
    lockno_manage: 'للاطلاع فقط: تعديل صلاحيات الأدوار يحتاج صلاحية «تعديل صلاحيات الأدوار».',
    lockpartner_role: 'دور الشريك يعدّله شريك فقط.',
    partnerNoRemove:
      'يمكن إضافة صلاحيات لدور الشريك، ولا يمكن سحب أي صلاحية منه: الشركاء يرون كل شيء.',
    locknot_held: 'لا تملكها أنت، فلا يمكنك منحها أو سحبها',
    locksegregated: 'مخصصة لدور واحد عن قصد (فصل المهام)',
    lockpartnerKeep: 'لا تُسحب من دور الشريك',
    pending: 'تغييرات غير محفوظة: إضافة {{add}}، إزالة {{remove}}',
    reset: 'تراجع',
    review: 'مراجعة وحفظ',
    confirmTitle: 'حفظ صلاحيات دور: {{name}}',
    confirmMembers:
      'سيسري التغيير فوراً على {{count}} شخص يحملون هذا الدور، ويُبلَّغ جميع الشركاء.',
    willAdd: 'ستُضاف',
    willRemove: 'ستُزال',
    reasonHint:
      'لماذا تتغيّر صلاحيات هذا الدور؟ (5 أحرف على الأقل). يُحفظ في سجل التدقيق ويصل للشركاء.',
    saved: 'تم حفظ صلاحيات الدور.',
    notFound: 'هذا الدور غير موجود.',
    modules: {
      users: 'الموظفون والأدوار',
      org: 'الفروع والمواقع',
      devices: 'الأجهزة والجلسات',
      settings: 'الإعدادات',
      approvals: 'الموافقات',
      audit: 'التدقيق',
      emergency: 'وضع الطوارئ',
      ledger: 'الدفاتر المحاسبية',
      cash: 'الصناديق النقدية',
      sync: 'المزامنة',
      notifications: 'الرسائل',
      backups: 'النسخ الاحتياطي',
      inventory: 'المخزن والمواد',
      purchasing: 'المشتريات والموردون',
      production: 'الإنتاج',
      supply: 'تجهيز الفروع',
      counts: 'الجرد',
    },
  },
  account: {
    title: 'حسابي',
    profile: 'بياناتي',
    sessions: 'أجهزتي المسجّلة',
    sessionsIntro:
      'الأجهزة والمتصفحات التي سجّلت الدخول منها. إذا لم تعرف أحدها، أنهِ جلسته وغيّر كلمة المرور.',
    thisDevice: 'هذا الجهاز',
    signedInAt: 'تاريخ الدخول',
    lastUsed: 'آخر استخدام',
    ip: 'عنوان IP',
    endSession: 'إنهاء الجلسة',
    unknownBrowser: 'متصفح غير معروف',
  },
  inbox: {
    title: 'الإشعارات',
    empty: 'لا توجد إشعارات.',
    unreadOnly: 'غير المقروءة فقط',
    markRead: 'تحديد كمقروء',
    read: 'مقروء',
    severityINFO: 'معلومة',
    severityWARNING: 'تنبيه',
    severityCRITICAL: 'عاجل',
  },
  placeholder: {
    title: 'هذه الشاشة قيد البناء',
    body: 'ستكون جاهزة في الجزء التالي من المرحلة الأولى، بعد ملاحظاتك على هذه النسخة.',
  },
  notFound: {
    title: 'الصفحة غير موجودة',
    home: 'العودة للرئيسية',
  },
  syncIssues: {
    title: 'مشاكل المزامنة',
    subtitle:
      'عمليات سجّلتها الأجهزة بدون إنترنت ورفضها الخادم عند الإرسال. لا يُحذف شيء تلقائياً: كل مشكلة تنتظر قراراً من شخص.',
    filterStatus: 'الحالة',
    statusOPEN: 'مفتوحة',
    statusRESOLVED: 'محلولة',
    empty: 'لا توجد مشاكل بهذه الشروط.',
    occurredAt: 'وقت العملية',
    who: 'الموظف والجهاز',
    what: 'العملية',
    why: 'سبب الرفض',
    category: 'النوع',
    categoryFINANCE: 'مالية',
    categoryOPERATIONS: 'تشغيلية',
    open: 'فتح',
    detailTitle: 'مشكلة مزامنة',
    payload: 'بيانات العملية كما أُرسلت',
    location: 'الموقع',
    device: 'الجهاز',
    noDevice: 'بدون جهاز',
    resolution: 'الحل',
    resolutionDISCARDED: 'أُهملت',
    resolutionRETRIED_WITH_EDIT: 'أُعيدت بعد التصحيح',
    resolutionCOMPENSATED: 'سُجّل قيد تعويضي',
    resolvedBy: 'حلّها {{name}} في {{time}}',
    financeRule:
      'مشكلة مالية: لا يمكن إغلاقها مباشرة. اكتب اقتراحك (إهمال، أو إعادة بعد التصحيح، أو قيد تعويضي) ويوافق عليه المحاسب قبل التنفيذ، ويظهر في تقرير الشركاء اليومي.',
    proposalOpen: 'يوجد اقتراح بانتظار الموافقة على هذه المشكلة.',
    openProposal: 'فتح الاقتراح',
    discard: 'إهمال',
    retry: 'تصحيح وإعادة الإرسال',
    proposeDiscard: 'اقتراح إهمال',
    proposeRetry: 'اقتراح تصحيح وإعادة',
    discardTitle: 'إهمال العملية',
    discardIntro: 'العملية لن تُنفَّذ أبداً. يُحفظ السبب في سجل التدقيق.',
    retryTitle: 'تصحيح العملية وإعادة إرسالها',
    retryIntro:
      'صحّح البيانات ثم أعد الإرسال. تُنفَّذ العملية باسمك وبصلاحياتك، وإذا رُفضت مرة أخرى تبقى المشكلة مفتوحة.',
    businessDate: 'تاريخ العمل',
    businessDateHint: 'اختياري: استخدمه إذا كان شهر العملية مغلقاً مثلاً.',
    note: 'السبب',
    noteHint: 'اشرح ما حدث ولماذا هذا هو الحل الصحيح (10 أحرف على الأقل).',
    invalidJson: 'القيمة ليست بصيغة JSON صحيحة.',
    retried: 'نُفِّذت العملية بعد التصحيح وأُغلقت المشكلة.',
    proposed: 'أُرسل الاقتراح للمحاسب للموافقة.',
    proposeCompensate: 'اقتراح قيد تعويضي',
    compensateTitle: 'اقتراح قيد تعويضي',
    compensateIntro:
      'القيد التعويضي يسجّل في الدفاتر ما حدث فعلاً بدلاً من العملية المرفوضة. مثال: وُجد نقص 5,000 د.ع في الصندوق اليومي ← مدين: فروقات نقدية قيد المراجعة 5,000، دائن: الصندوق اليومي 5,000. يوافق عليه المحاسب قبل أن يُسجَّل.',
    entryDate: 'تاريخ القيد',
    entryDateHint: 'اختياري: إذا تُرك فارغاً يُسجَّل بتاريخ يوم الموافقة.',
    compensationEntry: 'عرض القيد التعويضي',
  },
  approvals: {
    title: 'الموافقات',
    subtitle:
      'الطلبات الحساسة التي تحتاج موافقة قبل تنفيذها (تصحيح قيد، فترة مغلقة، فرق نقدي، مشكلة مزامنة مالية).',
    filterStatus: 'الحالة',
    filterType: 'نوع الطلب',
    empty: 'لا توجد طلبات بهذه الشروط.',
    type: 'نوع الطلب',
    requestedBy: 'مقدّم الطلب',
    requestedAt: 'تاريخ الطلب',
    reason: 'السبب',
    status: 'الحالة',
    approvals: 'الموافقات المطلوبة',
    approvalsValue: '{{given}} من {{required}}',
    open: 'فتح',
    statusPENDING: 'بانتظار القرار',
    statusAPPROVED: 'موافق عليه — لم يُنفَّذ',
    statusREJECTED: 'مرفوض',
    statusCANCELLED: 'ملغى',
    statusEXECUTED: 'نُفِّذ',
    back: 'كل الطلبات',
    detail: 'تفاصيل الطلب',
    payload: 'محتوى الطلب',
    decisions: 'القرارات',
    noDecisions: 'لم يُتخذ أي قرار بعد.',
    decisionAPPROVE: 'موافقة',
    decisionREJECT: 'رفض',
    result: 'نتيجة التنفيذ',
    executedBy: 'نفّذه',
    cancelledBy: 'ألغاه',
    lastError: 'سبب فشل التنفيذ',
    lastErrorHint:
      'تمت الموافقة لكن التنفيذ فشل. أصلح السبب ثم أعد المحاولة، أو يلغي مقدّم الطلب الطلب.',
    approve: 'موافقة',
    reject: 'رفض',
    decideTitle: 'قرارك على الطلب',
    decideIntro: 'قرارك نهائي ويُسجَّل باسمك. يُبلَّغ جميع الشركاء بكل موافقة على طلب مالي.',
    comment: 'تعليق',
    commentHintApprove: 'اختياري عند الموافقة.',
    commentHintReject: 'مطلوب عند الرفض: اشرح السبب لمقدّم الطلب.',
    commentRequired: 'اكتب سبب الرفض.',
    executeAgain: 'إعادة محاولة التنفيذ',
    cancel: 'إلغاء الطلب',
    cancelTitle: 'إلغاء الطلب',
    proposedEntry: 'القيد المقترح',
    openEntry: 'فتح القيد في الدفاتر',
    notYours: 'لا يمكنك اتخاذ قرار على طلب قدّمته أنت.',
  },
  ledger: {
    title: 'الدفاتر المحاسبية',
    subtitle:
      'كل دينار دخل أو خرج مسجّل هنا تلقائياً. هذه الشاشات للاطلاع فقط: لا يُعدَّل أي قيد ولا يُحذف، والتصحيح يكون بقيد عكسي بموافقة أحد الشركاء.',
    book: 'الدفتر',
    bookHint:
      'دفتر الشركة يجمع فرع طريق بغداد والمخزن والإدارة. لكل فرع فرنشايز دفتر منفصل خاص به.',
    noBooks: 'لا توجد دفاتر يمكنك الاطلاع عليها.',
    tabJournal: 'القيود (دفتر اليومية)',
    tabAccounts: 'الحسابات وأرصدتها',
    tabTrial: 'ميزان المراجعة',
    tabPeriods: 'الأشهر المحاسبية',
    tabVerify: 'فحص السلامة',
    howToRead: 'كيف تقرأ القيد؟',
    howToReadBody:
      'كل عملية مالية تُسجَّل في طرفين متساويين: المدين والدائن. القاعدة البسيطة: المدين هو الحساب الذي أخذ المال أو القيمة (مثل الصندوق عند البيع، أو المصروف عند الدفع)، والدائن هو الحساب الذي أعطاها (مثل المبيعات، أو الصندوق عند دفع مصروف). مجموع المدين يساوي مجموع الدائن في كل قيد دائماً.',
    debit: 'مدين',
    debitPlain: 'الطرف الآخذ',
    credit: 'دائن',
    creditPlain: 'الطرف المعطي',
    entryNo: 'رقم القيد',
    date: 'التاريخ',
    description: 'البيان (وصف العملية)',
    amount: 'المبلغ',
    postedBy: 'سجّله',
    postedAt: 'وقت التسجيل',
    system: 'النظام',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
    show: 'عرض',
    emptyJournal: 'لا توجد قيود في هذه المدة.',
    loadOlder: 'عرض قيود أقدم',
    reversedBadge: 'معكوس',
    reversalBadge: 'قيد عكسي',
    emergencyBadge: 'وضع الطوارئ',
    sources: {
      cash_transfer_send: 'تسليم نقد (إرسال)',
      cash_transfer_receive: 'استلام نقد',
      cash_transfer_cancel: 'إلغاء تسليم نقد',
      cash_transfer_resolution: 'حسم فرق نقدي',
      ledger_adjustment: 'قيد تصحيح',
      ledger_opening_balance: 'رصيد افتتاحي',
      ledger_reversal: 'قيد عكسي',
      sync_issue_compensation: 'قيد تعويضي لمشكلة مزامنة',
    },
    typeASSET: 'الأصول',
    typeASSETHint: 'ما تملكه الشركة: النقد في الصناديق، الأرصدة الإلكترونية، المخزون، المعدات.',
    typeLIABILITY: 'الخصوم (الالتزامات)',
    typeLIABILITYHint: 'ما على الشركة للآخرين: الموردون، الرواتب المستحقة.',
    typeEQUITY: 'حقوق الملكية',
    typeEQUITYHint: 'حصص الشركاء الأربعة ومسحوباتهم والأرباح المحتجزة.',
    typeREVENUE: 'الإيرادات',
    typeREVENUEHint: 'ما دخل من المبيعات وعوائد الفرنشايز وغيرها.',
    typeEXPENSE: 'المصاريف',
    typeEXPENSEHint: 'ما صُرف لتشغيل المطعم: المواد، الرواتب، الإيجار، الكهرباء…',
    code: 'الرمز',
    account: 'الحساب',
    balance: 'الرصيد',
    groupTotal: 'مجموع {{name}}',
    headerAccount: 'حساب تجميعي',
    inactive: 'موقوف',
    openStatement: 'كشف الحساب',
    accountsHint:
      'الرصيد حتى اليوم بكل القيود المسجلة. الحساب التجميعي يعرض مجموع الحسابات التي تحته. اضغط على أي حساب لرؤية كشف حسابه.',
    trialHint:
      'قائمة بكل حساب فيه حركة ورصيده في جهة المدين أو الدائن. إذا تساوى المجموعان فالدفاتر متوازنة وسليمة حسابياً.',
    asOf: 'حتى تاريخ',
    location: 'الموقع',
    allLocations: 'كل المواقع',
    totals: 'المجموع',
    balanced: 'متوازن: مجموع المدين = مجموع الدائن',
    unbalanced: 'غير متوازن! أبلغ الشركاء وتقنية المعلومات فوراً.',
    emptyTrial: 'لا توجد حركة حتى هذا التاريخ.',
    statementTitle: 'كشف حساب',
    statementHint: 'كل حركة على هذا الحساب في المدة المختارة، والرصيد بعد كل حركة.',
    opening: 'الرصيد في بداية المدة (قبل {{date}})',
    closing: 'الرصيد في نهاية المدة',
    balanceAfter: 'الرصيد بعدها',
    periodTotals: 'مجموع حركة المدة',
    truncated:
      'المدة طويلة: عُرضت أول {{count}} حركة فقط، والرصيد في نهاية الجدول هو رصيد آخر حركة معروضة. اختر مدة أقصر لرؤية الباقي.',
    emptyStatement: 'لا توجد حركة على هذا الحساب في هذه المدة.',
    backToLedger: 'الدفاتر',
    balanceMeaningASSET: 'الرصيد = المبلغ الموجود في هذا الحساب.',
    balanceMeaningLIABILITY: 'الرصيد = المبلغ الذي على الشركة دفعه.',
    balanceMeaningEQUITY: 'الرصيد = حصة الشركاء في هذا الحساب.',
    balanceMeaningREVENUE: 'الرصيد = مجموع ما دخل (منذ بداية التسجيل).',
    balanceMeaningEXPENSE: 'الرصيد = مجموع ما صُرف (منذ بداية التسجيل).',
    headerNoStatement:
      'هذا حساب تجميعي لا تُسجَّل عليه قيود مباشرة. افتح أحد الحسابات التي تحته من شاشة الحسابات.',
    entryTitle: 'القيد رقم {{no}}',
    lines: 'أطراف القيد',
    memo: 'ملاحظة',
    entryReason: 'سبب العكس أو التصحيح',
    sourceLabel: 'نوع العملية',
    reverses: 'هذا القيد يعكس القيد رقم {{no}}',
    reversedBy: 'عُكس هذا القيد بالقيد رقم {{no}}',
    byApproval: 'سُجّل بعد موافقة',
    openApproval: 'فتح الموافقة',
    emergencyNote: 'سُجّل أثناء وضع الطوارئ. هذا مسجّل في سجل التدقيق وقد أُبلغ الشركاء.',
    immutable:
      'القيود لا تُعدَّل ولا تُحذف أبداً. لتصحيح خطأ يُسجَّل قيد عكسي بموافقة أحد الشركاء، ويبقى القيدان ظاهرين للأبد.',
    requestReverse: 'طلب عكس القيد',
    reverseTitle: 'طلب عكس القيد رقم {{no}}',
    reverseIntro:
      'العكس لا يحذف القيد: يُسجَّل قيد جديد بنفس المبالغ في الاتجاه المعاكس، ويبقى الاثنان ظاهرين. يُرسل الطلب للشركاء للموافقة.',
    requestReason: 'السبب',
    requestReasonHint: 'اشرح الخطأ بوضوح (10 أحرف على الأقل). يراه الشركاء قبل القرار.',
    requestSent: 'أُرسل الطلب للشركاء. تابعه من صفحة الموافقات.',
    sendRequest: 'إرسال الطلب',
    periodsHint:
      'كل شهر يبقى مفتوحاً للتسجيل حتى يُغلق. بعد مراجعة الشهر يغلقه المحاسب، فلا يُسجَّل فيه أي قيد بعد ذلك إلا بموافقة أحد الشركاء. تُغلق الأشهر بالترتيب: الأقدم أولاً.',
    month: 'الشهر',
    periodOPEN: 'مفتوح',
    periodCLOSED: 'مغلق',
    closedBy: 'أغلقه',
    notEnded: 'لم ينتهِ بعد',
    closeMonth: 'إغلاق الشهر',
    closeTitle: 'إغلاق شهر {{month}}',
    closeIntro:
      'بعد الإغلاق لن يُقبل أي قيد جديد بتاريخ هذا الشهر (العمليات المتأخرة من الأجهزة تظهر كمشاكل مزامنة مالية). إعادة الفتح تحتاج موافقة أحد الشركاء. تأكد أن الشهر روجع قبل الإغلاق.',
    closeDone: 'أُغلق شهر {{month}}.',
    closeEarlierFirst: 'أغلق الشهر الأقدم أولاً',
    requestReopen: 'طلب إعادة فتح',
    reopenTitle: 'طلب إعادة فتح شهر {{month}}',
    reopenIntro:
      'يُرسل الطلب للشركاء. بعد الموافقة يُفتح الشهر ويمكن التسجيل فيه مرة أخرى، ثم يُغلق من جديد.',
    verifyTitle: 'فحص سلامة الدفاتر',
    verifyHint:
      'كل قيد مختوم ببصمة رقمية مرتبطة بالقيد الذي قبله. إذا غيّر أحد أي رقم أو حذف قيداً مباشرة من قاعدة البيانات، يكشفه هذا الفحص.',
    verifyNow: 'افحص الآن',
    verifyIntact: 'سليم: فُحص {{count}} قيد ولم يتغيّر أي شيء.',
    verifyBroken: 'تحذير: القيد رقم {{no}} تغيّر أو حُذف بعد تسجيله. أبلغ الشركاء فوراً.',
    verifyNotAllowed: 'هذا الفحص متاح لمن يملك صلاحية فحص السجلات (الشركاء ومدير التدقيق).',
    addLine: 'إضافة سطر',
    removeLine: 'حذف السطر {{n}}',
    lineN: 'السطر {{n}}',
    chooseAccount: 'اختر الحساب…',
    accountRequired: 'اختر الحساب.',
    totalDebit: 'مجموع المدين',
    totalCredit: 'مجموع الدائن',
    difference: 'الفرق',
    linesBalanced: 'متوازن',
    lineAmountMissing: 'اكتب المبلغ في المدين أو في الدائن.',
    lineBothSides: 'السطر الواحد يكون مديناً أو دائناً، وليس الاثنين معاً.',
    linesTooFew: 'القيد يحتاج إلى سطرين على الأقل.',
    linesUnbalanced:
      'القيد غير متوازن: الفرق {{amount}}. يجب أن يتساوى مجموع المدين ومجموع الدائن.',
    amountInvalid: 'اكتب المبلغ بالأرقام فقط، بالدينار الكامل (مثل 25,000).',
    amountNotWhole: 'المبالغ بالدينار الكامل بدون كسور.',
    amountTooLarge: 'المبلغ كبير جداً.',
  },
  emergencyPage: {
    title: 'وضع الطوارئ',
    subtitle:
      'صلاحية استثنائية لمسؤول تقنية المعلومات لتصحيح بيانات مالية بشكل عاجل، مع إبلاغ الشركاء فوراً.',
    whatItDoes:
      'عند التفعيل: يُبلَّغ الشركاء الأربعة فوراً (إشعار وواتساب وبريد)، وكل ما تفعله يُعلَّم في سجل التدقيق، وينتهي الوضع تلقائياً بعد المدة المحددة. يمكن لأي شريك إنهاؤه في أي وقت.',
    activateTitle: 'تفعيل وضع الطوارئ',
    password: 'كلمة المرور',
    code: 'رمز تطبيق المصادقة',
    reason: 'السبب',
    reasonHint:
      'اشرح ما الذي يجب تصحيحه ولماذا لا يمكن انتظار موافقة الشركاء (20 حرفاً على الأقل).',
    minutes: 'المدة',
    minutesDefault: 'المدة الافتراضية من الإعدادات',
    minutesValue: '{{count}} دقيقة',
    activate: 'تفعيل وضع الطوارئ',
    activeTitle: 'وضع الطوارئ مُفعّل الآن',
    activeLeft: 'الوقت المتبقي: {{time}}',
    endNow: 'إنهاء الآن',
    endTitle: 'إنهاء وضع الطوارئ',
    endIntro: 'يُرسل ملخص بكل ما حدث خلاله إلى جميع الشركاء.',
    note: 'ملاحظة',
    history: 'سجل جلسات الطوارئ',
    noHistory: 'لم يُفعّل وضع الطوارئ من قبل.',
    startedAt: 'البداية',
    activatedBy: 'فعّله',
    duration: 'المدة',
    state: 'الحالة',
    stateActive: 'مُفعّل الآن',
    stateEARLY: 'أُنهي مبكراً',
    stateEXPIRED: 'انتهت مدته',
    endedBy: 'أنهاه: {{name}}',
    actions: 'عدد العمليات: {{count}}',
    seeActions: 'عرض ما تم خلالها في سجل التدقيق',
    notForYou: 'تفعيل وضع الطوارئ خاص بمسؤول تقنية المعلومات. يمكنك هنا متابعة الجلسات وإنهاؤها.',
  },
  settingsPage: {
    title: 'الإعدادات',
    subtitle:
      'قواعد العمل التي يستخدمها النظام. كل تغيير يُحفظ مع السبب ومن قام به، ولا يُمحى القديم: يمكن دائماً معرفة القيمة التي كانت معمولاً بها في أي وقت.',
    locationHint: 'القيم الخاصة بكل فرع تظهر حسب الموقع المختار في الأعلى: {{name}}.',
    chooseLocation: 'لرؤية قيمة فرع معيّن أو تغييرها، اختر الفرع من الأعلى.',
    groups: {
      approval: 'الموافقات',
      franchise: 'الفرنشايز',
      auth: 'الدخول والأمان',
      emergency: 'وضع الطوارئ',
      hub: 'الفروع عند انقطاع الإنترنت',
      sync: 'المزامنة',
      other: 'أخرى',
    },
    current: 'المعمول به الآن',
    defaultValue: 'القيمة الافتراضية: {{value}}',
    usingDefault: 'لم تُغيَّر بعد (القيمة الافتراضية)',
    range: 'المسموح: من {{min}} إلى {{max}}',
    rangeMin: 'المسموح: {{min}} أو أكثر',
    rangeMax: 'المسموح: حتى {{max}}',
    lastChange: 'آخر تغيير: {{name}}، {{time}}',
    lastChangeReason: 'السبب: {{reason}}',
    companyValue: 'قيمة الشركة',
    locationValue: 'قيمة هذا الفرع',
    locationUsesCompany: 'هذا الفرع يستخدم قيمة الشركة.',
    scheduled: 'تغيير مجدول: {{value}} يبدأ {{time}}',
    sensitive: 'يُبلَّغ الشركاء',
    sensitiveHint: 'أي تغيير في هذا الإعداد يُرسَل فوراً إلى جميع الشركاء.',
    change: 'تغيير',
    changeHere: 'تغيير لهذا الفرع',
    history: 'السجل',
    noEditRight: 'لا تملك صلاحية تغيير هذا الإعداد.',
    minutes: '{{count}} دقيقة',
    yes: 'نعم',
    no: 'لا',
    changeTitle: 'تغيير الإعداد',
    changeTitleHere: 'تغيير الإعداد لفرع: {{name}}',
    newValue: 'القيمة الجديدة',
    percentHint: 'اكتب النسبة المئوية، مثلاً 5 أو 5.25',
    minutesHint: 'بالدقائق',
    startWhen: 'يبدأ العمل به',
    startNow: 'الآن',
    startLater: 'في وقت لاحق (بتوقيت بغداد)',
    startAt: 'الوقت (بتوقيت بغداد)',
    reasonHint: 'لماذا تغيّر هذا الإعداد؟ (5 أحرف على الأقل). يُحفظ مع التغيير ويراه الشركاء.',
    saved: 'تم حفظ التغيير.',
    historyTitle: 'سجل تغييرات: {{name}}',
    historyEmpty: 'لم يُغيَّر هذا الإعداد من قبل؛ المعمول به هو القيمة الافتراضية.',
    setAt: 'وقت التغيير',
    startsAt: 'يبدأ',
    value: 'القيمة',
    where: 'النطاق',
    who: 'من',
    reason: 'السبب',
  },
  audit: {
    title: 'سجل التدقيق',
    subtitle:
      'كل تغيير في النظام: من قام به، ومتى، ومن أي جهاز، وما الذي تغيّر ولماذا. لا يمكن لأحد تعديل أو حذف أي سطر فيه.',
    filters: 'تصفية',
    from: 'من يوم',
    to: 'إلى يوم',
    entityType: 'نوع السجل',
    action: 'العملية',
    location: 'الموقع',
    anyValue: 'الكل',
    onlyEmergency: 'فقط ما تم داخل وضع الطوارئ',
    clearFilters: 'إزالة كل التصفيات',
    activeFilters: 'تصفيات من شاشة أخرى:',
    filterActor: 'الشخص: {{name}}',
    filterEntity: 'السجل: {{id}}',
    filterSession: 'جلسة طوارئ واحدة',
    remove: 'إزالة',
    when: 'الوقت',
    who: 'من',
    what: 'ماذا',
    reason: 'السبب',
    system: 'النظام (تلقائي)',
    device: 'من جهاز: {{name}}',
    emergencyMark: 'وضع الطوارئ',
    notSealed: 'لم يُختم بعد',
    notSealedHint: 'يُختم السطر في سلسلة الحماية بعد ثوانٍ من كتابته.',
    noRows: 'لا توجد سطور تطابق التصفية.',
    loadMore: 'عرض المزيد',
    showDetails: 'التفاصيل',
    hideDetails: 'إخفاء التفاصيل',
    field: 'الحقل',
    before: 'قبل',
    after: 'بعد',
    changedFields: 'الحقول التي تغيّرت: {{fields}}',
    showAllFields: 'عرض كل الحقول',
    showChangedOnly: 'عرض ما تغيّر فقط',
    noData: 'لا توجد بيانات إضافية لهذه العملية.',
    redacted: 'مخفي (بيانات سرية)',
    redactedChanged: 'مخفي (تغيّرت قيمته)',
    empty: '—',
    recordId: 'رقم السجل',
    requestIdLabel: 'رقم الطلب',
    ip: 'عنوان IP',
    rowId: 'رقم سطر التدقيق',
    session: 'جلسة الطوارئ',
    personActivity: 'نشاط هذا الشخص',
    recordHistory: 'تاريخ هذا السجل',
    actionINSERT: 'إضافة',
    actionUPDATE: 'تعديل',
    actionDELETE: 'حذف',
    events: {
      auth_login: 'تسجيل دخول',
      auth_logout: 'تسجيل خروج',
      auth_password_reset: 'تغيير كلمة المرور برمز لمرة واحدة',
      auth_refresh_token_reuse: 'محاولة استخدام جلسة قديمة (أُنهيت الجلسات)',
      auth_session_revoked: 'إنهاء جلسة',
      ledger_entry_posted: 'تسجيل قيد',
      sync_command_refused: 'رفض أمر مزامنة',
      auth_idle_sign_out: 'خروج تلقائي من حاسبة مشتركة بعد عدم الاستخدام',
      auth_phone_sessions_replaced: 'إنهاء دخول سابق على نفس الهاتف',
      device_phone_registered: 'تسجيل هاتف',
      device_connection_changed: 'تغيير اتصال الحاسبة بالجهاز الرئيسي',
      backup_office_copy_manifest: 'سحب نسخة المكتب الاحتياطية',
      attachment_view_link: 'رابط عرض لصورة مستند مالي',
    },
    chainTitle: 'فحص سلامة السجل',
    chainHint:
      'كل سطر يُختم مع السطر الذي قبله، فإذا غيّر أحدٌ أو حذف سطراً مباشرةً في قاعدة البيانات يظهر ذلك هنا.',
    chainNotAllowed: 'فحص السلامة لمن يملك صلاحية «فحص سلامة سجل التدقيق» على مستوى الشركة.',
    chainCheck: 'افحص الآن',
    chainIntact: 'السجل سليم: لم يُغيَّر ولم يُحذف أي سطر مختوم.',
    chainBroken:
      'تحذير: السلسلة مكسورة عند السطر رقم {{seq}}. تم تغيير أو حذف بيانات مباشرةً في قاعدة البيانات. أبلغ الشركاء فوراً.',
    chainSealed: 'سطور مختومة: {{count}}',
    chainUnsealed: 'بانتظار الختم: {{count}}',
    chainLatest: 'بصمة آخر سطر مختوم (رقم {{seq}})',
    chainLatestHint:
      'احتفظ بهذه البصمة. إذا حُذفت سطور من آخر السجل لا يكشفها إلا مقارنة البصمة ببصمة سابقة، وستُرسل يومياً في تقرير الشركاء لاحقاً.',
    chainCheckedAt: 'وقت الفحص: {{time}}',
    entities: {
      users: 'المستخدمون',
      user_role_assignments: 'إسناد الأدوار',
      roles: 'الأدوار',
      role_permissions: 'صلاحيات الأدوار',
      permissions: 'قائمة الصلاحيات',
      offline_policies: 'صلاحيات العمل بدون إنترنت',
      locations: 'الفروع والمواقع',
      setting_values: 'قيم الإعدادات',
      setting_definitions: 'تعريفات الإعدادات',
      devices: 'الأجهزة',
      phone_store_losses: 'قواعد هواتف تعذّر فتحها',
      device_enrollment_codes: 'رموز تسجيل الأجهزة',
      sessions: 'جلسات الدخول',
      user_notifications: 'الإشعارات',
      ledger_books: 'الدفاتر المحاسبية',
      accounts: 'الحسابات',
      accounting_periods: 'الأشهر المحاسبية',
      journal_entries: 'القيود',
      cash_boxes: 'الصناديق النقدية',
      cash_transfers: 'تسليم النقد',
      cash_transfer_settlements: 'تسوية فروقات النقد',
      approval_types: 'أنواع الموافقات',
      approval_rules: 'قواعد الموافقات',
      approval_requests: 'طلبات الموافقة',
      approval_decisions: 'قرارات الموافقة',
      emergency_sessions: 'جلسات وضع الطوارئ',
      sync_issues: 'مشاكل المزامنة',
      sync_commands: 'أوامر المزامنة',
      sync_probes: 'سجلات اختبار المزامنة',
      push_tokens: 'رموز إشعارات الهواتف',
      offline_verifiers: 'بيانات الدخول بدون إنترنت على الحاسبات',
      printers: 'الطابعات',
      office_copy_reports: 'تقارير نسخة المكتب',
      office_copy_releases: 'السماح لنسخة المكتب بالمتابعة',
      item_categories: 'أصناف المواد',
      items: 'المواد',
      item_units: 'وحدات الشراء',
      item_location_settings: 'إعدادات المواد في المواقع (الحد الأدنى)',
      item_prices: 'أسعار التحويل',
      attachments: 'الصور',
      document_attachments: 'صور المستندات',
      stock_batches: 'دفعات المواد',
      stock_movements: 'حركات المخزون',
      waste_documents: 'مستندات الهدر والتالف',
      waste_lines: 'سطور الهدر والتالف',
      opening_documents: 'مستندات الرصيد الافتتاحي',
      opening_stock_lines: 'سطور الرصيد الافتتاحي للمخزون',
      document_corrections: 'تصحيحات المستندات',
      suppliers: 'الموردون',
      purchase_invoices: 'فواتير الشراء',
      purchase_invoice_lines: 'أسطر فواتير الشراء',
      goods_receipts: 'استلامات البضاعة',
      goods_receipt_rows: 'سطور عدّ الاستلام',
      goods_receipt_lines: 'نتائج الاستلام لكل سطر فاتورة',
      goods_receipt_damage_photos: 'صور التالف عند الاستلام',
      purchase_excess_decisions: 'قرارات الزيادة عند الاستلام',
      photo_handoffs: 'صور أُرسلت بالهاتف (رمز QR)',
      photo_handoff_photos: 'صور تسليم الهاتف',
    },
  },
  emergency: {
    banner: 'وضع الطوارئ مُفعّل — كل ما تفعله يُسجَّل ويُبلَّغ به الشركاء. ينتهي بعد {{time}}.',
  },
  hub: {
    title: 'حالة الجهاز الرئيسي',
    subtitle: 'هذا الجهاز هو الجهاز الرئيسي (Hub) في {{location}}',
    link: 'حالة الجهاز الرئيسي',
    notHub: 'هذا الجهاز ليس الجهاز الرئيسي لموقعه.',
    back: 'رجوع',
    sending: 'الإرسال إلى الخادم',
    stateOnline: 'متصل بالخادم',
    stateOffline: 'لا اتصال بالخادم: العمليات محفوظة وتُرسل لاحقاً',
    stateStarting: 'قيد التشغيل',
    stateRevoked: 'ألغى قسم التقنية تسجيل هذا الجهاز',
    ownPending: 'أُدخلت على هذا الجهاز ولم تُرسل بعد',
    ownPendingHint: 'هذه العمليات موجودة على هذا الجهاز فقط حتى تُرسل (لا تطفئه قبل ذلك).',
    relayed: 'من الأجهزة الأخرى، تنتظر الإرسال',
    rejected: 'رفضها الخادم، تحتاج مراجعة',
    lastSync: 'آخر مزامنة ناجحة',
    never: 'لم تحدث بعد',
    lastError: 'آخر مشكلة',
    firstDownload: 'ما زال ينزّل بيانات الموقع لأول مرة؛ وخلال ذلك تتصل الأجهزة بالخادم مباشرة.',
    lan: 'الشبكة الداخلية',
    listening: 'تصل إليه أجهزة الفرع على',
    notListening: 'لا تستطيع الأجهزة الأخرى الوصول إليه',
    port: 'المنفذ',
    addresses: 'العناوين',
    noAddress: 'لم يُعثر على شبكة',
    mdnsOn: 'يُعثر عليه تلقائياً على الشبكة',
    mdnsOff: 'غير معلَن على الشبكة',
    fingerprint: 'بصمة الشهادة',
    fingerprintHint:
      'الأجهزة الأخرى لا تقبل هذا الجهاز الرئيسي إلا بهذه البصمة. يستطيع قسم التقنية مقارنتها بشاشة الأجهزة.',
    fingerprintRegistered: 'مسجّلة في الخادم',
    fingerprintNotRegistered: 'لم تُسجّل في الخادم بعد (ستُسجّل عند توفر الإنترنت)',
    windows: 'شبكة ويندوز',
    profilePublic: 'عامة',
    profilePrivate: 'خاصة',
    profileDomainAuthenticated: 'نطاق الشركة',
    profileUnknown: 'غير معروفة',
    firewall: 'قاعدة جدار الحماية',
    firewallPresent: 'موجودة (للشبكات الخاصة)',
    firewallMissing: 'غير موجودة',
    firewallDisabled: 'معطّلة',
    firewallWrongProfile: 'ليست للشبكات الخاصة',
    firewallUnknown: 'تعذّر الفحص',
    addRule: 'أضف القاعدة (تحتاج صلاحية المسؤول)',
    ruleNotChanged: 'لم تُضف القاعدة (رُفض طلب صلاحية المسؤول أو رفضها ويندوز).',
    recheck: 'افحص مرة أخرى',
    checkedAt: 'فُحص {{time}}',
    clients: 'الأجهزة المتصلة منذ تشغيل الجهاز الرئيسي',
    noClients: 'لم يتصل أي جهاز بعد.',
    clientName: 'الجهاز',
    clientAddress: 'العنوان',
    clientLastSeen: 'آخر اتصال',
    unknownPc: 'جهاز غير معروف',
    closingTitle: 'بانتظار إرسال كل شيء',
    closingBody: 'ما زالت {{count}} عملية قيد الإرسال. يُغلق البرنامج نفسه عندما تُرسل كلها.',
    keepWorking: 'استمر في العمل',
    warnPublicTitle: 'شبكة هذا الجهاز مضبوطة على «عامة»',
    warnPublicBody:
      'على الشبكة العامة يمنع ويندوز الأجهزة الأخرى من الوصول إلى الجهاز الرئيسي. اجعلها خاصة: الإعدادات ← الشبكة والإنترنت ← الشبكة المستخدمة (Ethernet أو Wi-Fi) ← نوع ملف تعريف الشبكة ← «شبكة خاصة». البرنامج لا يغيّر هذا بنفسه أبداً.',
    warnFirewallTitle: 'قاعدة جدار الحماية للجهاز الرئيسي غير موجودة',
    warnFirewallBody:
      'بدونها قد يمنع ويندوز الأجهزة الأخرى. أضفها بالزر أدناه (يطلب ويندوز صلاحية المسؤول).',
    warnPortTitle: 'المنفذ {{port}} يستخدمه برنامج آخر',
    warnPortBody:
      'أغلق ذلك البرنامج، أو اطلب من قسم التقنية اختيار منفذ آخر لهذا الفرع (الإعدادات ← منفذ الجهاز الرئيسي). يحاول الجهاز مرة أخرى كل بضع ثوانٍ.',
    warnListenTitle: 'تعذّر على الجهاز الرئيسي فتح منفذ الشبكة',
    warnCertTitle: 'الشهادة غير مسجّلة في الخادم بعد',
    warnCertBody: 'تُسجّل تلقائياً عند توفر الإنترنت.',
    warnMdnsTitle: 'تعذّر على الجهاز الرئيسي الإعلان عن نفسه على الشبكة',
    warnMdnsBody: 'ما زال بإمكان الأجهزة الأخرى الوصول إليه بعنوانه.',
    pairingTitle: 'ربط جهاز بهذا الجهاز الرئيسي',
    pairingIntro:
      'تحتاجه فقط إذا استُبدل الجهاز الرئيسي والإنترنت مقطوع: اكتب هذا الرمز على الجهاز الآخر في صفحة «الاتصال بالجهاز الرئيسي». مع الإنترنت يتم الربط تلقائياً.',
    pairingShow: 'إظهار رمز الربط',
    pairingNew: 'رمز جديد',
    pairingHide: 'إخفاء الرمز',
    pairingExpires: 'صالح حتى {{time}}. لا تعطه إلا لأجهزة هذا الفرع.',
    offlineTitle: 'الدخول بدون إنترنت',
    offlineAccounts: 'أشخاص يمكنهم الدخول بدون إنترنت في هذا الموقع',
    offlinePending: 'عمليات دخول لم تُبلَّغ للخادم بعد',
    offlineValidUntil: 'قائمتهم صالحة بدون الخادم حتى {{time}}.',
    offlineNone: 'لم تصل القائمة من الخادم بعد (تصل عند أول اتصال بالإنترنت).',
    offlineHint:
      'يتحقق هذا الجهاز من كلمة المرور أو الرمز السري لموظفي هذا الموقع عندما ينقطع الإنترنت، لنفسه ولباقي أجهزة الفرع.',
  },
  client: {
    title: 'الاتصال بالجهاز الرئيسي',
    subtitle: 'كيف يصل هذا الجهاز إلى الجهاز الرئيسي (Hub) في {{location}}',
    notClient: 'هذه الصفحة لأجهزة الكمبيوتر العادية المسجّلة في موقع.',
    linkHub: 'عبر الجهاز الرئيسي',
    linkServer: 'مباشرة إلى الخادم',
    linkAlone: 'يعمل وحده',
    linkNotYourHub: 'ليس جهازك الرئيسي',
    stateSearching: 'جارٍ البحث عن الجهاز الرئيسي…',
    stateConnected: 'متصل بالجهاز الرئيسي على {{address}}',
    stateNotPaired: 'لا يعرف هذا الجهاز شهادة الجهاز الرئيسي بعد',
    stateNotPairedHint:
      'يعرفها تلقائياً من الخادم عند توفر الإنترنت. بدون إنترنت: اربطه بالرمز الظاهر على الجهاز الرئيسي (أدناه).',
    stateNotFound: 'لم يُعثر على الجهاز الرئيسي على الشبكة',
    stateNotFoundHint: 'تأكد أن الجهاز الرئيسي يعمل وأنه على نفس شبكة الفرع، أو اكتب عنوانه أدناه.',
    stateNotYourHub: 'الجهاز على العنوان {{address}} ليس جهازك الرئيسي',
    stateNotYourHubHint:
      'قدّم شهادة مختلفة، لذلك لم يُرسَل إليه أي شيء. إذا استُبدل الجهاز الرئيسي: مع الإنترنت يُعرف الجديد تلقائياً، وبدون إنترنت اربط هذا الجهاز بالرمز الظاهر على الجهاز الرئيسي الجديد.',
    stateNotReady: 'الجهاز الرئيسي ما زال ينزّل بيانات الموقع لأول مرة',
    stateUnreachable: 'الجهاز الرئيسي لا يستجيب',
    stateUnreachableHint: 'تأكد أنه يعمل وأن كابل الشبكة أو الواي فاي متصل.',
    stateRefused: 'الجهاز الرئيسي لا يقبل هذا الجهاز بعد',
    stateRefusedHint: 'يتعرّف الجهاز الرئيسي على الأجهزة الجديدة عندما يتصل بالإنترنت.',
    fallbackServer: 'العمليات تُرسل الآن إلى الخادم مباشرة، ولا يضيع شيء.',
    fallbackAlone:
      'لا إنترنت ولا جهاز رئيسي: هذا الجهاز يحفظ عملياته على قرصه ويرسلها تلقائياً عند عودة الاتصال.',
    sending: 'عمليات هذا الجهاز',
    pending: 'لم تصل إلى الخادم بعد',
    atHub: 'منها محفوظة عند الجهاز الرئيسي',
    rejected: 'رفضها الخادم، تحتاج مراجعة',
    via: 'آخر إرسال',
    viaHub: 'عبر الجهاز الرئيسي',
    viaServer: 'مباشرة إلى الخادم',
    viaNone: 'لا يوجد اتصال الآن',
    firstDownload: 'ما زال ينزّل بيانات الموقع لأول مرة.',
    trustedTitle: 'الجهاز الرئيسي الموثوق',
    trustedNone: 'لا يوجد بعد',
    trustedFromServer: 'عُرفت شهادته من الخادم',
    trustedFromPairing: 'رُبط برمز الربط في {{time}}',
    presented: 'بصمة الجهاز الذي أجاب',
    findTitle: 'العثور على الجهاز الرئيسي',
    modeAuto: 'تلقائياً على الشبكة',
    modeManual: 'بعنوان مكتوب: {{address}}',
    address: 'عنوان الجهاز الرئيسي',
    addressHint: 'مثال: 192.168.1.10 أو 192.168.1.10:8443. منفذ هذا الفرع: {{port}}.',
    useAddress: 'استخدم هذا العنوان',
    useAuto: 'ابحث تلقائياً',
    searchAgain: 'ابحث مرة أخرى',
    found: 'الأجهزة الرئيسية الظاهرة على الشبكة لهذا الموقع',
    foundNone: 'لا شيء حتى الآن.',
    foundTrusted: 'موثوق',
    foundOther: 'شهادة أخرى',
    discoveryError: 'تعذّر البحث على الشبكة؛ يمكنك كتابة العنوان.',
    pairTitle: 'الربط برمز',
    pairIntro:
      'على الجهاز الرئيسي: افتح «حالة الجهاز الرئيسي» واضغط «إظهار رمز الربط»، ثم اكتب الرمز هنا. لا حاجة لذلك عند توفر الإنترنت.',
    pairCode: 'رمز الربط (6 أرقام)',
    pairAddress: 'عنوان الجهاز الرئيسي (اختياري)',
    pairAddressHint: 'اتركه فارغاً ليُبحث عنه تلقائياً.',
    pair: 'اربط',
    paired: 'تم الربط: هذا الجهاز يثق الآن بهذا الجهاز الرئيسي.',
    addressSaved: 'حُفظ العنوان.',
    errINVALID_ADDRESS: 'العنوان غير صحيح. اكتب مثلاً 192.168.1.10 أو 192.168.1.10:8443.',
    errINVALID_PAIRING_CODE: 'رمز الربط 6 أرقام.',
    errPAIR_NO_HUB_FOUND: 'لم يُعثر على الجهاز الرئيسي. اكتب عنوانه في الحقل الثاني.',
    errPAIR_UNREACHABLE: 'لا يوجد جهاز يستجيب على هذا العنوان.',
    errPAIR_NOT_A_HUB: 'الجهاز على هذا العنوان ليس جهازاً رئيسياً لبرنامج Dob.',
    errPAIRING_CLOSED:
      'لا يظهر رمز ربط على الجهاز الرئيسي الآن (أو انتهت صلاحيته). اضغط «إظهار رمز الربط» عليه ثم حاول مرة أخرى.',
    errPAIR_UNKNOWN_DEVICE:
      'الجهاز الرئيسي لا يعرف هذا الجهاز بعد: يحتاج الجهاز الرئيسي إلى الإنترنت مرة واحدة بعد تسجيل هذا الجهاز.',
    errPAIR_WRONG_CODE: 'الرمز لا يطابق. تأكد من الرمز الظاهر على الجهاز الرئيسي ومن العنوان.',
    errPAIR_OTHER_LOCATION: 'هذا الجهاز الرئيسي تابع لموقع آخر.',
    errOther: 'لم يتم. حاول مرة أخرى.',
    accessTitle: 'تغيير الاتصال يحتاج تسجيل دخول',
    accessNeedSignIn:
      'الربط، أو كتابة عنوان الجهاز الرئيسي، أو البحث التلقائي: يحتاج دخول مدير هذا الفرع أو تقنية المعلومات على هذا الجهاز (بالإنترنت أو بدونه). الحالة أعلاه ظاهرة للجميع.',
    accessNotAllowed:
      '{{name}} لا يملك صلاحية تغيير اتصال هذا الجهاز. يحتاج مدير هذا الفرع أو تقنية المعلومات.',
    accessServerUnreachable:
      'لا يمكن التأكد من الدخول الحالي بدون الخادم. سجّل الدخول بدون إنترنت.',
    accessSignIn: 'تسجيل الدخول',
    accessBy: 'يمكن تغيير الاتصال الآن: {{name}} ({{session}})',
    sessionOnline: 'بالإنترنت',
    sessionOffline: 'بدون إنترنت',
    errSIGN_IN_REQUIRED: 'سجّل الدخول كمدير هذا الفرع أو تقنية المعلومات أولاً.',
    errNOT_ALLOWED: 'هذا الحساب لا يملك صلاحية تغيير اتصال هذا الجهاز.',
    errSERVER_UNREACHABLE: 'لا يمكن التأكد من الدخول بدون الخادم. سجّل الدخول بدون إنترنت.',
  },
  offline: {
    title: 'الدخول بدون إنترنت',
    subtitle: 'لأعمال محدودة على هذا الجهاز فقط',
    intro:
      'الخادم لا يستجيب. يمكن الدخول على هذا الجهاز بدون إنترنت لأعمال محدودة، ويتحقق منه الجهاز الرئيسي أو هذا الجهاز.',
    choose: 'الدخول بدون إنترنت',
    backToOnline: 'العودة إلى الدخول العادي',
    identifier: 'رقم الهاتف',
    identifierHint: 'مثال: 07701234567',
    method: 'طريقة الدخول',
    pin: 'الرمز السري (PIN)',
    password: 'كلمة المرور',
    required: 'هذا الحقل مطلوب.',
    submit: 'دخول بدون إنترنت',
    checking: 'جارٍ التحقق…',
    r_bad_secret:
      'رقم الهاتف أو كلمة المرور / الرمز السري غير صحيح. إذا لم تضع رمزاً سرياً بعد، ادخل بكلمة المرور.',
    r_unknown_user:
      'رقم الهاتف أو كلمة المرور / الرمز السري غير صحيح. إذا لم تضع رمزاً سرياً بعد، ادخل بكلمة المرور.',
    r_locked: 'محاولات خاطئة كثيرة لهذا الرقم. حاول مرة أخرى بعد {{time}}.',
    r_device_locked: 'محاولات خاطئة كثيرة على هذا الجهاز. حاول مرة أخرى بعد {{time}}.',
    r_expired:
      'لم يتصل هذا الجهاز بالخادم منذ مدة طويلة، فلم يعد الدخول بدون إنترنت مسموحاً. يلزم الإنترنت مرة واحدة.',
    r_not_allowed: 'حسابك لا يسمح بالعمل بدون إنترنت على هذا الجهاز.',
    r_stale_secret:
      'تغيّرت كلمة المرور أو الرمز السري بعد آخر اتصال لهذا الجهاز. ادخل بالإنترنت مرة واحدة.',
    r_pin_not_allowed: 'حسابات الإدارة تدخل بكلمة المرور فقط، لا بالرمز السري.',
    r_password_too_short:
      'حسابك إداري، فالدخول بدون إنترنت يحتاج كلمة مرور من 16 حرفاً على الأقل. ادخل بالإنترنت مرة واحدة واختر كلمة مرور أطول.',
    r_hub_needed:
      'لا يستطيع هذا الجهاز التحقق منك وحده (لم تدخل عليه من قبل)، والجهاز الرئيسي لا يستجيب. ادخل عندما يعمل الجهاز الرئيسي أو الإنترنت.',
    r_new_device_not_allowed: 'الدخول بدون إنترنت مسموح فقط على جهاز دخلت عليه من قبل.',
    r_not_available: 'الدخول بدون إنترنت غير متاح على هذا الجهاز.',
    sessionOpen: 'أنت داخل بدون إنترنت باسم {{name}}.',
    continueOffline: 'المتابعة بدون إنترنت',
    homeTitle: 'تعمل بدون إنترنت',
    homeSignedIn: 'داخل بدون إنترنت: {{name}}',
    homeCan: 'ما يمكنك فعله بدون إنترنت على هذا الجهاز',
    allowed: 'مسموح',
    homeLimited: 'باقي البرنامج (التقارير، الإدارة، الإعدادات، الإقفال الشهري…) يحتاج الإنترنت.',
    homeLater:
      'شاشات العمل اليومي بدون إنترنت (الكاشير، الاستلام…) تُضاف في مراحلها القادمة. الآن: الاتصال بالجهاز الرئيسي.',
    homeDetails: 'تفاصيل الدخول',
    homeMethod: 'طريقة الدخول',
    homeCheckedBy: 'تحقّق منه',
    checkedByHub: 'الجهاز الرئيسي',
    checkedByPc: 'هذا الجهاز',
    homeSince: 'منذ',
    homeValidUntil: 'الدخول بدون إنترنت صالح على هذا الجهاز حتى',
    backOnline: 'عاد الاتصال بالخادم.',
    signInOnline: 'الدخول العادي',
    signOut: 'تسجيل الخروج',
  },
  updates: {
    title: 'تحديثات Dob Desktop',
    link: 'التحديثات',
    installed: 'النسخة المثبّتة',
    channel: 'النسخ التي يستلمها هذا الموقع',
    channelTest: 'نسخ التجربة (قبل الجميع)',
    channelStable: 'النسخ المستقرة',
    window: 'نافذة التحديث',
    windowValue: '{{start}} – {{end}} (بتوقيت بغداد)',
    windowHint:
      'تُثبَّت النسخة الجديدة في هذه النافذة فقط، وبعد إرسال كل عمليات هذا الجهاز. الجهاز الذي كان مطفأً وقت النافذة يتحدّث عند تشغيله التالي، قبل بدء العمل.',
    now: 'الآن',
    step_off: 'هذا الجهاز لا يحدّث نفسه (نسخة تطوير، أو لم يُسجَّل بعد).',
    step_paused:
      'التحديثات متوقفة على كل الأجهزة (إعداد الشركة «desktop_update.paused»). لا يُنزَّل ولا يُثبَّت شيء حتى يُلغي قسم تقنية المعلومات الإيقاف.',
    step_checking: 'جارٍ البحث عن نسخة جديدة…',
    step_up_to_date: 'البرنامج محدَّث.',
    step_not_yet:
      'النسخة {{version}} تصل إلى هذا الجهاز في {{time}} (النسخة الجديدة تصل إلى بعض الأجهزة أولاً).',
    step_download: 'جارٍ تنزيل النسخة {{version}}…',
    step_wait_window: 'النسخة {{version}} جاهزة، وستُثبَّت في {{time}}.',
    step_send_first: 'جارٍ إرسال العمليات المتبقية ({{count}}) قبل تثبيت النسخة {{version}}…',
    step_install: 'جارٍ تثبيت النسخة {{version}}…',
    step_installing: 'جارٍ تثبيت النسخة {{version}}. سيُغلق البرنامج ثم يفتح من جديد تلقائياً.',
    progress: '{{percent}}% ({{done}} من {{total}} ميغابايت)',
    lastCheck: 'آخر بحث عن نسخة جديدة',
    never: 'لم يحدث بعد',
    checkNow: 'ابحث عن نسخة جديدة الآن',
    checkNowHint: 'هذا بحث فقط. التثبيت يبقى في وقت النافذة.',
    error_FEED_UNREACHABLE:
      'تعذّر الوصول إلى خادم التنزيل (غالباً: لا يوجد إنترنت). ستُعاد المحاولة تلقائياً.',
    error_FEED_INVALID:
      'معلومات النسخة على خادم التنزيل غير صحيحة. لم يُثبَّت شيء. أبلغ قسم تقنية المعلومات.',
    error_SIGNATURE_INVALID:
      'النسخة الجديدة غير موقّعة من دوب بركر، لذلك لم تُنزَّل ولم تُثبَّت. أبلغ قسم تقنية المعلومات فوراً.',
    error_DOWNLOAD_FAILED: 'توقّف التنزيل. ستُعاد المحاولة تلقائياً.',
    error_CHECKSUM_MISMATCH:
      'الملف الذي نُزِّل ليس النسخة الموقّعة، لذلك لم يُثبَّت. أبلغ قسم تقنية المعلومات.',
    error_INSTALL_FAILED: 'تعذّر تشغيل برنامج التثبيت. ستُعاد المحاولة في النافذة التالية.',
    startedWithout_offline:
      'عند التشغيل تعذّر إجراء التحديث (لا يوجد إنترنت، أو تعذّر إرسال العمليات). فُتح الجهاز للعمل، وستُعاد محاولة التحديث في النافذة التالية أو عند التشغيل التالي.',
    startedWithout_too_slow:
      'عند التشغيل استغرق التحديث وقتاً طويلاً (إنترنت بطيء). فُتح الجهاز للعمل، ويستمر التنزيل، ويُثبَّت التحديث في النافذة التالية أو عند التشغيل التالي.',
    lastUpdate: 'تم التحديث من النسخة {{from}} إلى {{to}} في {{time}}.',
    beforeWorkTitle: 'جارٍ تحديث Dob Desktop قبل بدء العمل',
    beforeWorkBody:
      'كان هذا الجهاز مطفأً وقت نافذة التحديث، لذلك يتم التحديث الآن قبل بدء العمل: أولاً تُرسل كل العمليات إلى السيرفر، ثم تُثبَّت النسخة الجديدة. سيُغلق البرنامج ثم يفتح من جديد تلقائياً.',
    beforeWorkDontClose: 'لا تطفئ الجهاز.',
  },
  setup: {
    title: 'إعداد هذا الجهاز',
    subtitle: 'مرة واحدة فقط، عند أول تشغيل لبرنامج Dob Desktop',
    stepOf: 'الخطوة {{step}} من {{total}}',
    next: 'التالي',
    back: 'رجوع',
    tryAgain: 'حاول مرة أخرى',
    languageTitle: 'اختر اللغة',
    languageHint: 'يمكن تغييرها لاحقاً من أعلى الشاشة.',
    serverTitle: 'اختر الخادم',
    serverHint: 'الجهاز يعمل مع خادم واحد فقط. لتغييره لاحقاً يجب تسجيل الجهاز من جديد.',
    serverStagingHint: 'للتجربة والتدريب. البيانات فيه ليست حقيقية.',
    serverProductionHint: 'للعمل الحقيقي في الفروع.',
    serverDevelopmentHint: 'للمبرمج فقط، على هذا الجهاز.',
    serverChecking: 'جارٍ الاتصال بالخادم…',
    serverReachable: 'الخادم يستجيب.',
    serverUnreachable:
      'الخادم لا يستجيب. تأكد من الإنترنت ثم حاول مرة أخرى. التسجيل يحتاج إنترنت مرة واحدة فقط.',
    codeTitle: 'رمز تسجيل الجهاز',
    codeHint:
      'يُنشئ قسم تقنية المعلومات الرمز من لوحة الإدارة ← الأجهزة، لموقع ونوع جهاز محددين. صالح 30 دقيقة ولمرة واحدة.',
    code: 'الرمز',
    codePlaceholder: 'XXXX-XXXX',
    codeChecking: 'جارٍ التحقق…',
    confirmTitle: 'تأكد من الموقع ووضع الجهاز',
    confirmHint: 'هذه المعلومات من الرمز. إذا كانت غير صحيحة، ارجع واطلب رمزاً آخر.',
    location: 'الموقع',
    mode: 'وضع الجهاز',
    locationInactive: 'هذا الموقع غير مفتوح بعد في النظام. يمكن تجهيز الجهاز الآن.',
    modeHintHUB:
      'جهاز واحد فقط في الموقع. يبقى شغّالاً طوال العمل وموصولاً بـ UPS، ويخدم باقي أجهزة الموقع عبر الشبكة الداخلية حتى بدون إنترنت.',
    modeHintDESKTOP:
      'جهاز كاشير أو مكتب. يتصل بالجهاز الرئيسي للموقع، أو بالخادم مباشرة إذا كان الجهاز الرئيسي مطفأً.',
    modeHintBACKUP_AGENT:
      'جهاز في المكتب يسحب كل ليلة نسخة احتياطية مشفّرة من الخادم ويحتفظ بها. لا يحتاج أي منفذ مفتوح.',
    kioskCode: 'هذا الرمز لشاشة طلب ذاتي، ولا يصلح لهذا البرنامج. ارجع واطلب رمزاً لجهاز كمبيوتر.',
    warehouseTitle: 'تنبيه مهم: الجهاز الوحيد في المخزن',
    warehouseBody:
      'في المخزن جهاز واحد فقط، وهو الجهاز الرئيسي ومكان الإدخال معاً. العمليات التي تُسجَّل عليه ليس لها نسخة ثانية حتى تصل إلى الخادم. لذلك:',
    warehousePoint1:
      'لا تطفئ الجهاز قبل أن تُرسل كل العمليات. البرنامج ينبّهك عند الإطفاء إذا بقيت عمليات لم تُرسل.',
    warehousePoint2: 'صِل الجهاز بـ UPS حتى لا ينطفئ فجأة عند انقطاع الكهرباء.',
    warehousePoint3:
      'العمليات المحفوظة تبقى على القرص وتُرسل تلقائياً عند التشغيل التالي مع وجود الإنترنت.',
    warehouseAgree: 'قرأت التنبيه وفهمته',
    warehouseAgreeRequired: 'ضع علامة على «قرأت التنبيه وفهمته» للمتابعة.',
    pcName: 'اسم هذا الجهاز',
    pcNameHint: 'اسم يعرفه الموظفون، مثل «كاشير 1» أو «جهاز المحاسب». يظهر في صفحة الأجهزة.',
    register: 'تسجيل الجهاز',
    registering: 'جارٍ التسجيل…',
    doneTitle: 'تم تسجيل الجهاز',
    doneBody: 'سُجّل هذا الجهاز باسم «{{name}}» — {{mode}} في {{location}}.',
    goToBackupAgent: 'اختر مكان حفظ النسخة',
    goToSignIn: 'الذهاب إلى تسجيل الدخول',
    revokedTitle: 'أُلغي تسجيل هذا الجهاز',
    revokedBody:
      'ألغى قسم تقنية المعلومات تسجيل هذا الجهاز («{{name}}» في {{location}})، لذلك لا يمكن استخدامه حتى يُسجَّل من جديد. عُرف الإلغاء في {{time}}.',
    unknownTitle: 'الخادم لا يعرف هذا الجهاز',
    unknownBody:
      'رفض الخادم مفتاح هذا الجهاز («{{name}}» في {{location}}). ربما أُعيد ضبط خادم التجربة. إذا كان هذا غير متوقع، تواصل مع قسم تقنية المعلومات قبل التسجيل من جديد.',
    registerAgain: 'تسجيل الجهاز من جديد',
    registerAgainHint: 'تحتاج رمز تسجيل جديد من قسم تقنية المعلومات.',
    notDesktop: 'معالج الإعداد موجود في برنامج Dob Desktop فقط.',
    noProtection:
      'لا يستطيع ويندوز حماية مفتاح هذا الجهاز (التشفير غير متاح لهذا الحساب)، لذلك لا يمكن تسجيله.',
    alreadyRegistered: 'هذا الجهاز مسجّل بالفعل. لتغيير الخادم يجب إلغاء تسجيله أولاً.',
  },
  inventory: {
    itemsTitle: 'المواد',
    itemsSubtitle:
      'كل مواد المخزن: وحدة المخزون، وحدات الشراء، الحد الأدنى في كل موقع، وسعر التحويل للفروع. الكميات أرقام صحيحة دائماً (غرام، مليلتر، قطعة).',
    categoriesTitle: 'أصناف المواد',
    categoriesSubtitle:
      'كل مادة تتبع صنفاً. حساب الاستهلاك يحدد أين تُحسب تكلفة المادة عندما يستهلكها الفرع.',
    addItem: 'إضافة مادة',
    addCategory: 'إضافة صنف',
    editItemTitle: 'تعديل المادة: {{name}}',
    editCategoryTitle: 'تعديل الصنف: {{name}}',
    back: 'كل المواد',
    searchLabel: 'بحث',
    searchPlaceholder: 'اكتب جزءاً من الاسم أو الرمز',
    searchHint: 'لا فرق بين أ وا وإ، ولا بين ة وه، ولا بين ى وي.',
    categoryFilter: 'الصنف',
    allCategories: 'كل الأصناف',
    statusFilter: 'الحالة',
    showActive: 'الفعّالة',
    showInactive: 'الموقوفة',
    showAll: 'الكل',
    count: '{{count}} مادة',
    empty: 'لا توجد مواد بعد.',
    emptySearch: 'لا توجد مادة تطابق البحث.',
    notFound: 'هذه المادة غير موجودة.',
    cannotManage: 'للاطلاع فقط: إضافة المواد وتعديلها لمدير المخزن.',
    code: 'الرمز',
    codeHint: 'حروف إنكليزية كبيرة وأرقام وشَرطة، مثل DRK-PEPSI-330. يُستعمل في Excel.',
    name: 'المادة',
    category: 'الصنف',
    stockUnit: 'وحدة المخزون',
    stockUnitHint:
      'تُختار مرة واحدة ولا تتغير أبداً: الوزن بالغرام (يُعرض بالكيلو)، السوائل بالمليلتر (تُعرض باللتر)، والعدد بالقطعة.',
    stockUnitFixed: 'وحدة المخزون لا تتغير بعد الإضافة.',
    unitG: 'وزن — غرام (يُعرض بالكيلو)',
    unitML: 'سائل — مليلتر (يُعرض باللتر)',
    unitPC: 'عدد — قطعة',
    shortKG: 'كغم',
    shortL: 'لتر',
    shortPC: 'قطعة',
    purchaseUnits: 'وحدات الشراء',
    purchaseUnitsHint:
      'مثل «كارتون = 24 قطعة» أو «كيس = 25 كغم». الكمية في الوحدة لا تتغير بعد الحفظ؛ لتغييرها أوقف الوحدة وأضف غيرها.',
    noUnits: 'لا وحدات شراء: تُشترى بوحدة المخزون نفسها.',
    addUnit: 'إضافة وحدة شراء',
    unitNameAr: 'اسم الوحدة بالعربي',
    unitNameEn: 'اسم الوحدة بالإنجليزي',
    unitSize: 'كم {{unit}} في الوحدة؟',
    unitSizeHint: 'مثال: 24 للكارتون. يُكتب بالكيلو أو اللتر للوزن والسوائل (25 = 25 كغم).',
    unitOf: '{{name}} = {{quantity}} {{unit}}',
    defaultPurchase: 'وحدة الشراء الافتراضية',
    defaultBadge: 'افتراضية',
    unitRetired: 'موقوفة',
    retireUnit: 'إيقاف الوحدة',
    restoreUnit: 'إعادة تفعيل',
    makeDefault: 'اجعلها الافتراضية',
    editUnitTitle: 'تعديل الوحدة: {{name}}',
    removeUnitRow: 'حذف هذا السطر',
    flags: 'خصائص المادة',
    tracksBatches: 'تُتابَع بالدفعات وتاريخ الانتهاء',
    shelfLifeDays: 'مدة الصلاحية بالأيام',
    shelfLifeHint:
      'للمواد المنتَجة: تاريخ الانتهاء = تاريخ الإنتاج + هذه المدة. اتركه فارغاً إن لم يلزم.',
    expiryAlertDays: 'التنبيه قبل الانتهاء بالأيام',
    expiryAlertHint: 'فارغ = الإعداد العام (7 أيام).',
    isPurchased: 'تُشترى من الموردين',
    isProduced: 'تُنتَج في المخزن (المكسر)',
    isSellableToBranches: 'تُصرف للفروع',
    isCountableForRecipes: 'تدخل في وصفات القائمة (المرحلة الخامسة)',
    notes: 'ملاحظات',
    active: 'فعّالة',
    inactive: 'موقوفة',
    deactivate: 'إيقاف المادة',
    activate: 'إعادة تفعيل المادة',
    deactivateHint: 'المادة الموقوفة تبقى في السجلات، ولا تظهر في القوائم ولا تُعطى سعراً جديداً.',
    details: 'البيانات',
    minimums: 'الحد الأدنى في كل موقع',
    minimumsHint:
      'تنبيه «مخزون منخفض» عندما ينزل الرصيد عن الحد. الموقع الذي لا يحفظ المادة لا تظهر فيه في القوائم ولا الجرد.',
    location: 'الموقع',
    minimum: 'الحد الأدنى',
    noMinimum: 'بلا حد',
    stocked: 'يحفظها',
    notStocked: 'لا يحفظها',
    editMinimum: 'تعديل',
    minimumTitle: 'الحد الأدنى في {{location}}',
    minimumLabel: 'الحد الأدنى ({{unit}})',
    minimumFieldHint: 'فارغ = بلا حد.',
    isStockedLabel: 'هذا الموقع يحفظ المادة',
    testLocation: 'تجربة آلية',
    price: 'سعر التحويل',
    pricesTitle: 'سعر التحويل للفروع',
    pricesHint:
      'سعر واحد لكل الفروع. التغيير يبدأ الآن أو في وقت لاحق تختاره، ولا يرجع للماضي أبداً. كل تغيير يُحفظ في التاريخ مع السبب.',
    noPrice: 'لم يُحدَّد سعر بعد',
    currentPrice: 'السعر الحالي',
    nextPrice: 'من {{time}}: {{price}}',
    perDisplay: 'لكل {{unit}}',
    perPurchaseUnit: 'لكل {{name}} ({{quantity}} {{unit}})',
    pricePer: '{{price}} {{per}}',
    changePrice: 'تغيير السعر',
    changePriceTitle: 'سعر التحويل: {{name}}',
    priceLabel: 'السعر (دينار)',
    priceHint: 'دنانير صحيحة بدون كسور.',
    priceRequired: 'اكتب السعر بالدينار.',
    perLabel: 'السعر لكل',
    startLabel: 'يبدأ',
    startNow: 'الآن',
    startLater: 'في وقت لاحق',
    startAt: 'وقت البدء (بتوقيت بغداد)',
    priceReasonHint: 'لماذا تغيّر السعر؟ (5 أحرف على الأقل). يُحفظ في التاريخ.',
    historyTitle: 'تاريخ الأسعار',
    historyEmpty: 'لا أسعار بعد.',
    effectiveFrom: 'يبدأ من',
    recordedBy: 'سجّله',
    reasonColumn: 'السبب',
    scheduled: 'مجدول',
    inEffect: 'المعمول به',
    past: 'سابق',
    history: 'السجل',
    saved: 'تم الحفظ.',
    priceSaved: 'تم حفظ السعر.',
    consumptionAccount: 'حساب الاستهلاك في الفروع',
    consumptionHint:
      'عندما يستهلك الفرع هذه المواد (من الجرد قبل نظام الكاشير)، تُحسب تكلفتها في هذا الحساب.',
    consumption_food_cost: 'تكلفة المواد الغذائية (5100)',
    consumption_packaging_cost: 'التغليف (5200)',
    consumption_cleaning: 'التنظيف والمستلزمات (6700)',
    consumption_other_expenses: 'مصاريف أخرى (6900)',
    sortOrder: 'الترتيب في القوائم',
    activeItems: '{{count}} مادة فعّالة',
    categoryActive: 'فعّال',
    categoryInactive: 'موقوف',
    deactivateCategory: 'إيقاف الصنف',
    activateCategory: 'إعادة تفعيل الصنف',
    categoriesEmpty: 'لا توجد أصناف بعد. أضف صنفاً أولاً ثم المواد.',
    quantityInvalid: 'اكتب رقماً، مثل 12.5',
    quantityDecimals: 'لا يُقبل أكثر من {{max}} مراتب بعد الفاصلة.',
    quantityWhole: 'القطع أرقام صحيحة بدون كسور.',
    quantityTooLarge: 'الرقم كبير جداً.',
    unitSizeTooSmall: 'يجب أن تحتوي الوحدة على أكثر من {{unit}} واحد.',
    photoTitle: 'صورة المادة',
    photoHint:
      'صورة واضحة للمادة: JPEG أو PNG أو WebP. تُصغَّر تلقائياً قبل الرفع، وتُحفظ في التخزين الخاص.',
    photoNone: 'لا صورة بعد.',
    photoAlt: 'صورة {{name}}',
    addPhoto: 'إضافة صورة',
    changePhoto: 'تغيير الصورة',
    removePhoto: 'إزالة الصورة',
    photoUploading: 'جارٍ تجهيز الصورة ورفعها…',
    photoSaved: 'تم حفظ الصورة.',
    photoRemoved: 'أُزيلت الصورة من المادة. تبقى محفوظة في السجل.',
    photoNotImage: 'هذا الملف ليس صورة. اختر صورة JPEG أو PNG أو WebP.',
    photoTooBig: 'الصورة أكبر من 10 ميغابايت حتى بعد التصغير. اختر صورة أصغر.',
    photoUploadFailed: 'تعذّر رفع الصورة إلى التخزين. تأكد من الإنترنت وحاول مرة أخرى.',
    photoLinkNote:
      'رابط الصورة يعمل دقيقة واحدة فقط، ولا يحصل عليه إلا من سجّل دخوله وله صلاحية رؤية المواد.',
    photoLoadFailed: 'تعذّر عرض الصورة. حدّث الصفحة.',
  },
  purchases: {
    suppliersTitle: 'الموردون',
    suppliersSubtitle:
      'الشركات والأشخاص الذين نشتري منهم. الأسماء لا تتكرر، فيُختار المورد الصحيح من القائمة.',
    newSupplier: 'مورد جديد',
    editSupplier: 'تعديل المورد',
    supplierNameAr: 'اسم المورد (عربي)',
    supplierNameEn: 'اسم المورد (إنكليزي، اختياري)',
    contactPerson: 'الشخص المسؤول',
    phones: 'أرقام الهاتف',
    phonesHint: 'حتى 3 أرقام، يفصل بينها فاصلة.',
    address: 'العنوان',
    category: 'الصنف',
    categoryHint: 'مثلاً: مشروبات، لحوم، تغليف.',
    notes: 'ملاحظات',
    paymentTerms: 'مدة الدفع (أيام)',
    paymentTermsHint: 'بعد كم يوم من تاريخ الفاتورة الآجلة يحين دفعها. 0 = فوراً.',
    termsDays: '{{n}} يوم',
    termsNow: 'فوراً',
    active: 'فعّال',
    inactive: 'موقوف',
    stopSupplier: 'إيقاف المورد',
    reactivateSupplier: 'إعادة تفعيل المورد',
    searchSuppliers: 'ابحث بالاسم أو الرقم أو الصنف',
    noSuppliers: 'لا يوجد موردون بعد.',
    showInactive: 'إظهار الموقوفين',
    backToSuppliers: 'كل الموردين',
    supplierInvoices: 'فواتير هذا المورد',
    changeReason: 'سبب التعديل',
    phoneSharedNote: 'تنبيه عن الأرقام',
    invoicesTitle: 'فواتير الشراء',
    invoicesSubtitle:
      'كل فاتورة مع صورة ورقتها. لا تُعدَّل بعد تسجيلها: تُلغى بسبب وتُسجَّل من جديد، والملغاة تبقى ظاهرة.',
    newInvoice: 'فاتورة جديدة',
    noInvoices: 'لا توجد فواتير بعد.',
    statusRECORDED: 'مسجّلة',
    statusRECEIVED: 'مستلمة',
    statusCANCELLED: 'ملغاة',
    allStatuses: 'كل الحالات',
    allSuppliers: 'كل الموردين',
    location: 'المخزن',
    chooseLocation: 'اختر المخزن.',
    supplier: 'المورد',
    chooseSupplier: 'اختر المورد.',
    searchSupplier: 'ابحث عن المورد',
    number: 'رقم الفاتورة',
    numberRequired: 'اكتب رقم الفاتورة كما هو على الورقة.',
    numberHint: 'كما هو على الورقة. نفس المورد ونفس الرقم لا يُسجَّلان مرتين.',
    invoiceDate: 'تاريخ الفاتورة',
    dateRequired: 'اختر تاريخ الفاتورة.',
    paymentKind: 'طريقة الدفع',
    paymentCREDIT: 'آجل',
    paymentCASH: 'نقدي',
    cashHint: 'الفاتورة النقدية مستحقة فوراً، ويدفعها المحاسب من الخزنة (الخطوة W6).',
    dueDate: 'تاريخ الاستحقاق',
    dueDateHint: 'اتركه فارغاً: تاريخ الفاتورة + مدة دفع المورد.',
    dueBeforeInvoice: 'تاريخ الاستحقاق قبل تاريخ الفاتورة.',
    discount: 'خصم على الفاتورة (د.ع)',
    discountHint: 'يُوزَّع على الأسطر حسب قيمتها، فيبقى المجموع مطابقاً بالدينار.',
    discountTooLarge: 'الخصم أكبر من مجموع الأسطر.',
    note: 'ملاحظة',
    lines: 'أسطر الفاتورة',
    addLine: 'إضافة سطر',
    removeLine: 'حذف السطر',
    chooseItem: 'اختر المادة.',
    unit: 'الوحدة',
    chooseUnit: 'اختر وحدة فعّالة لهذه المادة.',
    perDisplay: 'لكل {{unit}}',
    quantityInUnit: 'الكمية',
    quantityRequired: 'اكتب الكمية.',
    quantityInvalid: 'اكتب الكمية بالأرقام (حتى 3 أرقام بعد الفاصلة).',
    quantityNotWhole: 'هذه الكمية لا تعطي عدداً صحيحاً من الوحدة الأصغر. صحّحها.',
    price: 'السعر لكل وحدة (د.ع)',
    priceRequired: 'اكتب السعر.',
    priceWhole: 'السعر بالدينار الكامل، بلا كسور.',
    priceInvalid: 'اكتب السعر بالأرقام.',
    lineTotal: 'مجموع السطر',
    grossTotal: 'مجموع الأسطر',
    total: 'الإجمالي',
    priceJumpReason: 'سبب ارتفاع السعر',
    priceJumpReasonRequired:
      'السعر أعلى من آخر سعر بأكثر من الحد المسموح. اكتب السبب (5 أحرف على الأقل).',
    priceJumpHint:
      'أعلى من آخر سعر بأكثر من {{limit}}%. آخر سعر: {{price}} لكل {{unit}} — فاتورة «{{number}}» بتاريخ {{date}} من «{{supplier}}».',
    lastPriceHint: 'آخر سعر: {{price}} لكل {{unit}} ({{date}})',
    photos: 'صور الفاتورة',
    photoRequired: 'صورة واحدة على الأقل لورقة الفاتورة. بعد التسجيل لا تُحذف ولا تُبدَّل.',
    addPhoto: 'إضافة صورة',
    recordInvoice: 'سجّل الفاتورة',
    recordInvoiceTitle: 'فاتورة شراء جديدة',
    replaces: 'تحلّ محل الفاتورة الملغاة',
    replacesThis: 'هذه الفاتورة تُسجَّل بدلاً من الفاتورة الملغاة «{{number}}».',
    recordedBy: 'سجّلها',
    cancelInvoice: 'إلغاء الفاتورة',
    cancelTitle: 'إلغاء الفاتورة «{{number}}»',
    cancelIntro:
      'الإلغاء يُبقي الفاتورة ظاهرة للأبد مع صورها وسببك واسمك. بعده تستطيع تسجيلها من جديد بنفس الرقم.',
    cancelReasonHint: 'اكتب السبب بوضوح (10 أحرف على الأقل).',
    cancelledNotice: 'ألغاها {{name}} في {{time}}. السبب: {{reason}}',
    replacedBy: 'سُجّلت بدلاً منها الفاتورة «{{number}}».',
    replacesNotice: 'تحلّ محل الفاتورة الملغاة «{{number}}».',
    recordAgain: 'سجّلها من جديد',
    addPhotoTitle: 'إضافة صورة للفاتورة',
    addPhotoIntro: 'بعد التسجيل تُضاف الصور فقط ولا تُحذف. اكتب سبب الإضافة.',
    photoAddedLater: 'أُضيفت لاحقاً ({{name}}): {{reason}}',
    fullSizeHint: 'اضغط أي صورة لفتحها بالحجم الكامل وقراءة الأرقام الصغيرة.',
    detailTitle: 'فاتورة «{{number}}»',
    backToInvoices: 'كل الفواتير',
    priceJumped: 'سعر مرتفع',
    notReceivedYet: 'لم تُستلم البضاعة بعد. لا شيء في المخزن ولا في الدفاتر حتى الاستلام.',
    linesCount: '{{n}} سطر',
  },
  // W5-6: the photo with the phone through a QR code (the page's side).
  handoff: {
    start: 'صوّر بالهاتف',
    scanTitle: 'صوّر بهاتفك عبر هذا الرمز',
    step1: 'افتح تطبيق دوب ستاف على هاتفك.',
    step2: 'من «الإدارة» اختر «صوّر للحاسبة» ووجّه الكاميرا إلى هذا الرمز.',
    step3: 'صوّر الورقة كلها، أو أجزاءها إن كانت طويلة. تظهر الصور هنا.',
    validFor: 'الرمز صالح لمدة {{minutes}}:{{seconds}}',
    expired: 'انتهت صلاحية الرمز ولم تصل صورة.',
    newCode: 'رمز جديد',
    arrived: 'وصلت الصور من الهاتف: {{n}}',
    moreHint: 'يمكنك تصوير جزء آخر بالرمز نفسه ما دام صالحاً.',
    confirmedTitle: 'سُجّل أن الصور وصلت من الهاتف',
    confirmedHint:
      'هذا الجهاز بلا إنترنت: تُربط صور الهاتف بالمستند حين يصل إلى الخادم. إن لم تكن قد وصلت، يُرفض المستند بهذا السبب ولا يضيع شيء منه.',
    offlineTitle: 'لا يصل هذا الجهاز إلى الخادم الآن',
    offlineHint: 'حين يقول الهاتف «وصلت»، اضغط «وصلت الصورة».',
    confirm: 'وصلت الصورة',
    cancel: 'إلغاء التصوير بالهاتف',
    drop: 'لا تستخدم صور الهاتف',
  },
  receiving: {
    tasksTitle: 'استلام بضاعة',
    tasksSubtitle:
      'فواتير تنتظر وصول بضاعتها. اعدد ما وصل فعلاً: لا تظهر لك كميات الفاتورة ولا أسعارها.',
    noTasks: 'لا توجد فواتير تنتظر الاستلام.',
    recordedBySelf: 'أنت سجّلت هذه الفاتورة، فيستلم بضاعتها شخص آخر.',
    receive: 'استلام',
    linesToCount: '{{n}} مادة للعدّ',
    backToTasks: 'كل ما ينتظر الاستلام',
    receiveTitle: 'استلام الفاتورة «{{number}}»',
    blindIntro:
      'اعدد كل مادة كما وصلت، واكتب 0 إذا لم يصل منها شيء. لا تظهر هنا كميات الفاتورة ولا أسعارها، والنظام يقارن بعد الحفظ.',
    closedTask: 'هذه الفاتورة لم تعد تنتظر الاستلام.',
    received: 'وصل',
    damaged: 'منه تالف',
    damagedHint: 'اتركه فارغاً إذا لم يكن شيء تالفاً.',
    countIn: 'العدّ بـ',
    batchCode: 'رمز الدفعة (اختياري)',
    batchCodeHint: 'كما هو على العلبة. إذا تركته فارغاً يصنع النظام رمزاً.',
    productionDate: 'تاريخ الإنتاج (اختياري)',
    expiryDate: 'تاريخ الانتهاء',
    addBatch: 'دفعة أخرى من نفس المادة',
    removeRow: 'حذف هذا السطر',
    damagePhotoRequired: 'صوّر البضاعة التالفة: صورة واحدة على الأقل.',
    addDamagePhoto: 'تصوير التالف',
    unlistedTitle: 'بضاعة وصلت وليست في الفاتورة',
    unlistedHint: 'تُحفظ جانباً ولا تدخل المخزون حتى يقرّر مدير المخزن: فاتورة إضافية أو إرجاع.',
    addUnlisted: 'إضافة مادة ليست في الفاتورة',
    note: 'ملاحظة (اختياري)',
    confirm: 'احفظ الاستلام',
    confirmTitle: 'حفظ الاستلام',
    confirmIntro: 'بعد الحفظ لا يُعدَّل الاستلام. تأكّد من العدد ومن صور التالف.',
    done: 'سُجّل الاستلام. شكراً.',
    doneHint: 'يقارن النظام ما عددته بالفاتورة، ويُبلَّغ مدير المخزن إن وُجدت زيادة.',
    receivedRequired: 'اكتب كم وصل (0 إذا لم يصل شيء).',
    quantityInvalid: 'اكتب العدد بالأرقام (حتى 3 أرقام بعد الفاصلة للكيلو واللتر).',
    quantityNotWhole: 'هذا لا يعطي عدداً صحيحاً من القطع. صحّحه.',
    damagedTooMany: 'التالف أكثر مما وصل.',
    expiryRequired: 'اكتب تاريخ انتهاء هذه الدفعة.',
    datesOrder: 'تاريخ الإنتاج بعد تاريخ الانتهاء.',
    chooseItem: 'اختر المادة.',
    unlistedQuantity: 'اكتب كم وصل منها.',
    receiptTitle: 'الاستلام',
    receivedByAt: 'استلمها {{name}} في {{time}}',
    invoiced: 'في الفاتورة',
    accepted: 'دخل المخزون',
    claim: 'نقص أو تالف',
    excess: 'زيادة',
    acceptedValue: 'قيمة ما دخل المخزون',
    claimValue: 'مطالبة على المورد',
    claimHint:
      'النقص والتالف لا يدخلان المخزون: قيمتهما دين على المورد في حساب «مطالبات الموردين» حتى يُسوّى (الخطوة W6).',
    rowsTitle: 'ما عُدّ، سطراً سطراً',
    excessLeft: 'تنتظر القرار: {{n}}',
    excessWaitingLink: 'افتح «فروقات الاستلام» للقرار',
    fromExcessOf: 'فاتورة إضافية لزيادة استلام الفاتورة «{{number}}»',
    extraInvoiceLink: 'الفاتورة الإضافية «{{number}}»',
    returnLine: 'أُرجع {{quantity}} — {{name}}، {{time}}: {{reason}}',
    noteLabel: 'ملاحظة المستلم',
    excessTitle: 'فروقات الاستلام',
    excessSubtitle:
      'بضاعة وصلت زيادة على الفاتورة. لا تدخل المخزون حتى تقرّر: فاتورة إضافية من المورد، أو إرجاع بورقة موقّعة.',
    noExcess: 'لا زيادة تنتظر القرار.',
    waitingQuantity: 'تنتظر',
    receivedAt: 'وقت الاستلام',
    returnExcess: 'إرجاع',
    extraInvoice: 'فاتورة إضافية',
    returnTitle: 'إرجاع زيادة «{{item}}»',
    returnIntro:
      'الإرجاع يغلق هذه الزيادة دون أن تدخل المخزون. صوّر ورقة الإرجاع التي وقّعها سائق المورد.',
    returnQuantity: 'الكمية المرجعة ({{unit}})',
    returnQuantityHint: 'اتركه فارغاً لإرجاع كل ما بقي ({{n}}).',
    returnReason: 'السبب',
    returnReasonHint: 'مثلاً: أعادها سائق المورد معه (10 أحرف على الأقل).',
    returnPhotoRequired: 'صورة واحدة على الأقل لورقة الإرجاع الموقّعة.',
    addReturnPhoto: 'تصوير ورقة الإرجاع',
    extraIntro:
      'فاتورة إضافية لزيادة استلام الفاتورة «{{number}}». تُستلم فوراً من الزيادة التي عُدّت، وتتبع كل قواعد الفاتورة: صورة الورقة، رقم لا يتكرر، وسبب إذا ارتفع السعر.',
    extraNoExcess: 'لم يبقَ من زيادة هذا الاستلام شيء ينتظر القرار.',
    extraConfirmTitle: 'راجع الفاتورة الإضافية قبل حفظها',
    extraConfirmWarning:
      'لا يمكن إلغاء هذه الفاتورة بعد الحفظ، وتُستلم فوراً: تدخل بضاعتها المخزون ويُسجَّل مبلغها ديناً للمورد. راجع المورد والمواد والكميات والمجموع.',
    extraConfirmSave: 'تأكيد الحفظ',
    extraConfirmBack: 'رجوع للتعديل',
    statementTitle: 'كشف حساب المورد',
    statementHint:
      'كل ما ندين به لهذا المورد وما نطالبه به، من القيود نفسها. الدفعات وأعمار الديون تأتي مع الخطوة W6.',
    owed: 'المستحق للمورد',
    claims: 'المطالبات على المورد',
    noStatement: 'لا قيود لهذا المورد بعد: تُسجَّل عند استلام البضاعة.',
    date: 'التاريخ',
    entry: 'القيد',
    invoice: 'الفاتورة',
    accountPAYABLE: 'ذمم الموردين (2100)',
    accountCLAIMS: 'مطالبات الموردين (1360)',
    debit: 'مدين',
    credit: 'دائن',
    runningOwed: 'المستحق بعده',
    runningClaims: 'المطالبات بعده',
  },
  pcPurchases: {
    title: 'المشتريات على هذا الجهاز',
    intro:
      'الفاتورة والاستلام يُحفظان مع صورهما على هذا الجهاز أولاً، في خطوة واحدة، ثم يُرسلان حين يتوفر الإنترنت، مرة واحدة.',
    newInvoice: 'فاتورة جديدة على هذا الجهاز',
    invoicesHere: 'الفواتير المسجّلة على هذا الجهاز',
    invoicesHereEmpty: 'لم تُسجَّل فاتورة على هذا الجهاز بعد.',
    receiptsHere: 'الاستلامات المسجّلة على هذا الجهاز',
    receiptsHereEmpty: 'لم يُسجَّل استلام على هذا الجهاز بعد.',
    tasksHere: 'فواتير تنتظر استلام بضاعتها',
    savedInvoice: 'حُفظت الفاتورة وصورها على هذا الجهاز. تُرسل حين يتوفر الإنترنت، مرة واحدة.',
    savedReceipt: 'سُجّل الاستلام على هذا الجهاز. يُرسل حين يتوفر الإنترنت، مرة واحدة.',
    alreadySaved: 'هذا محفوظ على هذا الجهاز من قبل، ولم يُحفظ مرة ثانية.',
    stateWaiting: 'بانتظار الإرسال',
    stateArrived: 'وصلت',
    stateRefused: 'رفضها الخادم: {{reason}}',
    cancelWaiting: 'الإلغاء بانتظار الإرسال',
    cancelArrived: 'أُلغيت',
    cancelRefused: 'رُفض الإلغاء: {{reason}}',
    cancelHere: 'إلغاء',
    cancelHereTitle: 'إلغاء الفاتورة «{{number}}»',
    cancelHereIntro:
      'تُلغى الفاتورة قبل استلام بضاعتها فقط. إن كانت بضاعتها قد استُلمت على جهاز آخر، يرفض الخادم الإلغاء ويظهر السبب هنا.',
    cancelReason: 'سبب الإلغاء',
    keepInvoice: 'رجوع دون إلغاء',
    pickPhoto: 'اختيار صورة من الجهاز',
    photoHintInvoice:
      'صورة واحدة على الأقل لورقة الفاتورة. حاسوب المخزن بلا كاميرا: اختر صورة محفوظة عليه.',
    photoHintDamage: 'صوّر البضاعة التالفة، ثم اختر الصورة من ملفات الجهاز.',
    photosWaiting: 'صور لم تُرسل بعد: {{n}}',
    photosRefused: 'صور رفضها الخادم: {{n}} — أخبر قسم التقنية',
    lines: 'المواد: {{n}}',
    backToList: 'رجوع إلى القائمة',
    // Why the PC refused to save (said under the field; never a number of the invoice).
    unknown_supplier: 'هذا المورد غير موجود في نسخة هذا الجهاز.',
    inactive: 'هذا المورد موقوف.',
    date_in_future: 'تاريخ الفاتورة بعد اليوم.',
    invoice_number_taken: 'رقم الفاتورة مسجّل لهذا المورد على هذا الجهاز من قبل.',
    unknown_item: 'هذه المادة غير موجودة في نسخة هذا الجهاز.',
    not_purchased: 'هذه المادة لا تُشترى (موقوفة أو ليست مادة مشتراة).',
    invalid_choice: 'هذه الوحدة ليست وحدة فعّالة لهذه المادة.',
    price_jump_reason_required: 'السعر أعلى من آخر سعر بأكثر من الحد: اكتب سبب الزيادة.',
    discount_too_large: 'الخصم أكبر من مجموع الأسطر.',
    not_image: 'هذا الملف ليس صورة.',
    not_on_this_pc: 'تُلغى من هنا الفواتير المسجّلة على هذا الجهاز فقط.',
    already_received: 'سُجّل استلام هذه الفاتورة على هذا الجهاز من قبل.',
    not_waiting: 'هذه الفاتورة لا تنتظر استلاماً هنا.',
    second_person: 'أنت سجّلت هذه الفاتورة، فيستلم بضاعتها شخص آخر.',
    line_missing: 'لم تكتب ما وصل من كل مادة. اكتب 0 إذا لم يصل شيء.',
    damaged_more_than_received: 'التالف أكثر مما وصل.',
    other_item: 'هذا السطر لا يطابق الفاتورة.',
    unknown_line: 'هذا السطر لا يطابق الفاتورة.',
    expiry_required: 'هذه المادة تُحفظ بالدفعات: اكتب تاريخ الانتهاء.',
    dates_order: 'تاريخ الإنتاج بعد تاريخ الانتهاء.',
    batch_not_used: 'هذه المادة بلا دفعات.',
    damage_photo_required: 'صوّر البضاعة التالفة: صورة واحدة على الأقل.',
    invalid: 'تعذّر الحفظ: راجع الحقول.',
    NOT_A_WAREHOUSE: 'فواتير الشراء تُسجَّل على حاسوب في مخزن فقط.',
    SAVE_FAILED: 'تعذّر الحفظ على هذا الجهاز، ولم يُحفظ شيء. حاول مرة ثانية.',
  },
  stock: {
    wasteTitle: 'الهدر والتالف',
    wasteSubtitle: 'كل ما يخرج من المخزون تالفاً أو منتهياً أو فاسداً، بصورته وقيمته.',
    recordWaste: 'تسجيل هدر',
    recordWasteTitle: 'تسجيل هدر',
    wasteEmpty: 'لا هدر مسجَّل بعد.',
    wasteRecorded: 'سُجِّل الهدر.',
    reason: 'السبب',
    reasonEXPIRED: 'منتهي الصلاحية',
    reasonDAMAGED: 'تالف',
    reasonSPOILED: 'فاسد',
    reasonOTHER: 'سبب آخر',
    reasonText: 'اكتب السبب',
    note: 'ملاحظة (اختياري)',
    location: 'الموقع',
    chooseLocation: 'اختر الموقع',
    allLocations: 'كل المواقع',
    item: 'المادة',
    searchItem: 'ابحث عن مادة بالاسم أو الرمز',
    chooseItem: 'اختر المادة',
    noItemFound: 'لا مادة بهذا الاسم.',
    batch: 'الدفعة',
    chooseBatch: 'اختر الدفعة',
    batchOption: '{{code}} — تنتهي {{expiry}}',
    quantity: 'الكمية ({{unit}})',
    quantityHeader: 'الكمية',
    quantityRequired: 'اكتب الكمية.',
    moreThanAvailable: 'الكمية أكثر من المتوفر في هذا الموقع.',
    addLine: 'إضافة مادة',
    removeLine: 'حذف السطر',
    lineNo: 'السطر {{n}}',
    photos: 'الصور',
    addWastePhoto: 'إضافة صورة',
    photoRequired: 'صورة واحدة على الأقل: لا هدر بدون صورة.',
    photoUploading: 'جارٍ رفع الصورة…',
    photoReady: 'الصورة {{n}} جاهزة',
    photoNotArrived: 'لم تصل الصورة من الجهاز بعد',
    photoNotArrivedHint: 'يرسلها الجهاز الذي التُقطت عليه وحده عند اتصاله بالإنترنت.',
    value: 'القيمة',
    total: 'المجموع',
    recordedBy: 'سجّله',
    date: 'التاريخ',
    items: 'المواد',
    linesCount: '{{n}} سطر',
    back: 'رجوع',
    documentEntries: 'القيد المحاسبي',
    documentEntriesHint: 'قيد هذا المستند في الدفتر، وقيود تصحيحه إن صُحِّح.',
    entryNo: 'قيد رقم {{n}} — {{date}}',
    entryReverses: 'يعكس القيد رقم {{n}}',
    noEntry: 'لا قيد: البضاعة لم تكن لها قيمة بعد.',
    account: 'الحساب',
    debit: 'مدين',
    credit: 'دائن',
    corrected: 'صُحِّح',
    correctedNote: 'صُحِّح هذا المستند في {{time}} بموافقة شريك. السبب: {{reason}}',
    replacedBy: 'المستند الجديد',
    replaces: 'هذا المستند حلّ محل مستند صُحِّح.',
    openOriginal: 'المستند الأصلي',
    isReplacement: 'بديل',
    requestCorrection: 'طلب تصحيح',
    correctionTitle: 'طلب تصحيح مستند مُرحَّل',
    correctionIntro:
      'المستند المُرحَّل لا يُعدَّل أبداً. يُعكس بقيمه الأصلية — ويُسجَّل الصحيح مكانه إن وُجد — بعد موافقة شريك. يبقى الاثنان ظاهرين دائماً.',
    correctionReason: 'سبب التصحيح (10 أحرف على الأقل)',
    reverseOnly: 'عكسه فقط (لم يحدث هذا الهدر)',
    reverseAndReplace: 'عكسه وتسجيل الصحيح مكانه',
    reverseOpening: 'عكس هذا الرصيد الافتتاحي (ثم تُعدّ رصيداً جديداً)',
    replacementLines: 'الهدر الصحيح',
    reusePhotos: 'تُستعمل صور المستند الأصلي، ويمكنك إضافة غيرها.',
    correctionSent: 'أُرسل طلب التصحيح. ينتظر موافقة شريك.',
    pendingCorrection: 'لهذا المستند طلب تصحيح بانتظار الموافقة.',
    openRequest: 'فتح الطلب',
    openDocument: 'فتح المستند',
    sendRequest: 'إرسال الطلب',
    openingsTitle: 'الرصيد الافتتاحي للمخزون',
    openingsSubtitle:
      'ما في المخزن أو الفرع يوم بدء العمل بالنظام: المادة والكمية والقيمة. لا يُرحَّل إلا بموافقة شريك.',
    newOpening: 'رصيد افتتاحي جديد',
    openingsEmpty: 'لا رصيد افتتاحي بعد.',
    status: 'الحالة',
    statusDRAFT: 'مسودة',
    statusCANCELLED: 'ملغاة',
    statusPOSTED: 'مُرحَّل',
    statusREVERSED: 'معكوس',
    businessDate: 'تاريخ الرصيد',
    unitCost: 'التكلفة لكل {{unit}} (دينار)',
    unitCostRequired: 'اكتب التكلفة بالدينار (0 أو أكثر).',
    cost: 'التكلفة (دينار)',
    costPer: 'لكل',
    lineValue: 'قيمة السطر',
    batchCode: 'رقم الدفعة',
    productionDate: 'تاريخ الإنتاج (اختياري)',
    expiryDate: 'تاريخ الانتهاء',
    batchNeeded: 'هذه المادة تُحفظ بالدفعات: اكتب رقم الدفعة وتاريخ انتهائها.',
    draftSaved: 'حُفظت المسودة. راجعها ثم اطلب الترحيل.',
    requestPosting: 'طلب الترحيل',
    postingTitle: 'طلب ترحيل الرصيد الافتتاحي',
    postingIntro:
      'يوافق عليه شريك غيرك. بعد الموافقة يدخل المخزون ويُسجَّل القيد: مدين حساب المخزون / دائن الأرصدة الافتتاحية.',
    postingReason: 'ملاحظة للشريك (10 أحرف على الأقل)',
    postingSent: 'أُرسل طلب الترحيل. ينتظر موافقة شريك.',
    cancelDraft: 'إلغاء المسودة',
    cancelReason: 'سبب الإلغاء (5 أحرف على الأقل)',
    draftCancelled: 'أُلغيت المسودة.',
    preparedBy: 'أعدّه',
    onePerLocation:
      'رصيد افتتاحي واحد مُرحَّل لكل موقع. إن كان خطأً اطلب تصحيحه، ثم أعدّ رصيداً جديداً.',
    waitingApproval: 'بانتظار موافقة شريك',
    stockTitle: 'المخزون',
    hereTitle: 'المخزون حسب الموقع',
    hereSubtitle: 'ما في كل موقع الآن من كل مادة، مع دفعاتها. القيم لمن له صلاحية رؤية التكاليف.',
    hereSubtitleDesktop:
      'نسخة هذا الجهاز من بيانات الخادم لهذا الموقع — تعمل بدون إنترنت. القيم لمن له صلاحية رؤية التكاليف، عند الدخول العادي فقط.',
    hereTitleAt: 'المخزون في {{location}}',
    hereEmpty: 'لا مخزون في هذا الموقع بعد.',
    hereNoMatch: 'لا مادة تطابق البحث.',
    provisional: 'مؤقت',
    provisionalHint:
      'يشمل {{n}} عملية من هذا الموقع لم تصل إلى الخادم بعد (مثل هدر سُجّل بدون إنترنت). ما عليه «مؤقت» يصبح نهائياً حين يُقرّه الخادم.',
    localNOT_READY: 'هذا الجهاز ينسخ بيانات الخادم لأول مرة. انتظر قليلاً.',
    localNOT_REGISTERED: 'هذا الجهاز غير مسجّل في موقع بعد.',
    localSIGN_IN_REQUIRED: 'سجّل الدخول أولاً.',
    localNOT_ALLOWED: 'ليس لك صلاحية لهذا في هذا الموقع.',
    localSERVER_UNREACHABLE:
      'الخادم لا يستجيب، فلا يمكن التأكد من دخولك العادي. سجّل الدخول بدون إنترنت للعمل على نسخة هذا الجهاز.',
    backToOffline: 'رجوع',
    localWasteTitle: 'الهدر المسجّل على هذا الجهاز',
    localWasteIntro:
      'يُحفظ الهدر مع صوره على هذا الجهاز أولاً، في خطوة واحدة، ثم يُرسل حين يتوفر الإنترنت، مرة واحدة. لا تظهر الكميات المتوفرة هنا.',
    localWasteEmpty: 'لم يُسجَّل هدر على هذا الجهاز بعد.',
    localWasteSaved: 'حُفظ الهدر وصوره على هذا الجهاز. يُرسل حين يتوفر الإنترنت، مرة واحدة.',
    localWasteAlready: 'هذا الهدر محفوظ على هذا الجهاز من قبل، ولم يُحفظ مرة ثانية.',
    localPickPhoto: 'اختيار صورة من الجهاز',
    localPhotoHint:
      'صورة واحدة على الأقل. حاسوب المخزن بلا كاميرا: اختر صورة محفوظة عليه (مثل صورة نُقلت من الهاتف).',
    localLines: 'المواد: {{n}}',
    localStateWaiting: 'بانتظار الإرسال',
    localStateArrived: 'وصل',
    localStateRefused: 'رُفض: {{reason}}',
    localPhotosWaiting: 'صور لم تُرسل بعد: {{n}}',
    localPhotosRefused: 'صور رفضها الخادم: {{n}} — أخبر قسم التقنية',
    localInvalid: 'تعذّر حفظ الهدر: راجع الحقول.',
    stockEmpty: 'لا مخزون لهذه المادة بعد.',
    averageCost: 'متوسط التكلفة',
    perUnit: '{{price}} لكل {{unit}}',
    movementsTitle: 'حركة المادة',
    movementsHint:
      'كل دخول وخروج بالترتيب مع الرصيد بعده، ومن قام به ومن أي جهاز. القيم لمن له صلاحية رؤية التكاليف.',
    movementsEmpty: 'لا حركة بعد.',
    allKinds: 'كل الحركات',
    kind: 'الحركة',
    balanceAfter: 'الرصيد بعدها',
    by: 'بواسطة',
    document: 'المستند',
    reversed: 'عُكست',
    matchTitle: 'مطابقة المخزون مع الدفاتر',
    matchHint:
      'يفحص النظام كل ليلة الساعة 6:30 وبعد كل تحديث أن قيمة المخزون في كل موقع تساوي حسابه في الدفتر، وأن كل رصيد يساوي مجموع حركاته.',
    matchOk: 'متطابق',
    matchDifferent: 'يوجد فرق',
    matchNever: 'لم يُفحص بعد.',
    checkNow: 'افحص الآن',
    lastCheck: 'آخر فحص: {{time}} ({{kind}})',
    runNIGHTLY: 'الليلي',
    runDEPLOY: 'بعد التحديث',
    runON_DEMAND: 'بطلب',
    checked: '{{locations}} موقع، {{pools}} رصيد مادة',
    hiddenDifferences: 'و{{n}} فرق في مواقع لا تراها.',
    expected: 'المتوقع',
    actual: 'الموجود',
    check: 'الفحص',
    kindOPENING: 'رصيد افتتاحي',
    kindPURCHASE_RECEIPT: 'استلام مشتريات',
    kindSUPPLIER_RETURN: 'إرجاع للمورد',
    kindPRODUCTION_INPUT: 'مدخل إنتاج',
    kindPRODUCTION_OUTPUT: 'ناتج إنتاج',
    kindTRANSFER_OUT: 'شحن إلى فرع',
    kindTRANSFER_IN: 'استلام شحنة',
    kindTRANSIT_FOUND: 'عُثر عليه بعد الشحن',
    kindBRANCH_RETURN_OUT: 'إرجاع من الفرع',
    kindBRANCH_RETURN_IN: 'استلام مرتجع فرع',
    kindWASTE: 'هدر',
    kindCOUNT_ADJUSTMENT: 'تسوية جرد',
    kindCONSUMPTION: 'استهلاك',
    kindCOST_ADJUSTMENT: 'تسوية تكلفة',
    kindBATCH_RECLASS: 'نقل بين الدفعات',
    kindREVERSAL: 'عكس (تصحيح)',
    checkSTOCK_VS_LEDGER: 'قيمة المخزون لا تساوي حساب المخزون في الدفتر',
    checkWRONG_INVENTORY_ACCOUNT: 'قيد على حساب مخزون لا يخص هذا الموقع',
    checkTRANSIT_NOT_EMPTY: 'رصيد في «مخزون قيد النقل» ولا شحنات بعد',
    checkREVIEW_NOT_EMPTY: 'رصيد في «فروقات مخزون قيد المراجعة»',
    checkBALANCE_QUANTITY: 'كمية الرصيد لا تساوي مجموع حركاته',
    checkBALANCE_VALUE: 'قيمة الرصيد لا تساوي مجموع حركاته',
    checkBALANCE_MISSING: 'حركات بلا رصيد',
    checkBATCH_QUANTITY: 'كمية الدفعة لا تساوي مجموع حركاتها',
    checkPARTY_UNNAMED: 'سطور على حساب طرف بلا اسم الطرف',
  },
  staff: {
    appName: 'دوب ستاف',
    /** A version with the build number EAS gave it (owner, 2026-09-30). */
    versionWithBuild: '{{version}} (بناء {{build}})',
    /** Notifications on the phone (M8-3). Push text is never sensitive (D183). */
    push: {
      title: 'الإشعارات',
      channelName: 'تنبيهات دوب',
      shortOn: 'مفعّلة',
      shortOff: 'غير مفعّلة',
      checking: 'جارٍ التحقق من الإشعارات…',
      on: 'الإشعارات مفعّلة على هذا الهاتف.',
      notAsked: 'لم تسمح بعد بالإشعارات على هذا الهاتف. اضغط «السماح بالإشعارات» ثم «سماح».',
      blocked: 'الإشعارات غير مسموحة لهذا التطبيق. اضغط «السماح بالإشعارات» ثم «سماح».',
      blockedSettings:
        'الإشعارات ممنوعة لهذا التطبيق في إعدادات الهاتف. افتح الإعدادات ← الإشعارات، وفعّلها لتطبيق دوب ستاف.',
      notSetUp:
        'هذه النسخة من التطبيق لا تستقبل الإشعارات بعد (إعداد Firebase لم يكتمل). ستعمل في نسخة قادمة.',
      offline: 'تفعيل الإشعارات يحتاج إنترنت. سيُكمل التطبيق وحده عند عودة الإنترنت.',
      failed:
        'تعذّر تفعيل الإشعارات ({{code}}). حاول مرة أخرى، وإذا تكرر أرسل صورة هذه الشاشة لقسم تقنية المعلومات.',
      allow: 'السماح بالإشعارات',
      openSettings: 'فتح إعدادات الهاتف',
      retry: 'حاول مرة أخرى',
      test: 'أرسل لي إشعار تجربة',
      testSent:
        'أُرسل. يصل خلال ثوانٍ إلى هواتفك التي دخلت منها (إذا كان التطبيق مفتوحاً يظهر في أعلى الشاشة).',
      testNoPhone: 'لا يوجد هاتف مسجّل للإشعارات على حسابك بعد. انتظر قليلاً ثم حاول مرة أخرى.',
      testStub: 'الخادم لا يرسل الإشعارات فعلياً بعد (وضع التجربة)، لذلك لم يُرسل شيء.',
      privacy:
        'الإشعار لا يحمل مبالغ ولا رواتب ولا أي معلومة حساسة؛ التفاصيل تظهر داخل التطبيق فقط في «تنبيهاتي».',
    },
    selfCheck: {
      title: 'فحص التطبيق',
      subtitle: 'نسخة تجربة (M8-0): يتأكد أن التطبيق يعمل على هذا الهاتف.',
      running: 'جارٍ الفحص…',
      allPassed: 'كل الفحوص نجحت ({{passed}})',
      someFailed: 'فشل {{failed}} من الفحوص — أرسل صورة هذه الشاشة لقسم تقنية المعلومات',
      failedNames: 'التي فشلت:',
      ok: 'نجح',
      failed: 'فشل',
      info: 'للمعلومة',
      device: 'هذا الهاتف',
      runAgain: 'إعادة الفحص',
      duration: '{{ms}} ملّي ثانية',
    },
    signIn: {
      subtitle: 'تطبيق موظفي دوب بركر',
      identifier: 'رقم الهاتف',
      identifierHint: 'مثال: 07701234567 — يمكنك الكتابة بالأرقام العربية أو الإنجليزية.',
      needsInternet: 'الدخول يحتاج اتصالاً بالإنترنت. تأكد من الإنترنت ثم حاول مرة أخرى.',
      appCheck: 'فحص التطبيق',
      version: 'الإصدار {{version}}',
    },
    mfaSetup: {
      step1:
        'ثبّت تطبيق Google Authenticator أو Microsoft Authenticator على هذا الهاتف (من Google Play أو App Store).',
      step2:
        'اضغط «افتح تطبيق المصادقة» ليُضاف حسابك فيه تلقائياً، أو انسخ المفتاح وأضفه في التطبيق يدوياً.',
      step3: 'ارجع إلى هنا واكتب الرقم المكوّن من 6 أرقام الظاهر في التطبيق.',
      openApp: 'افتح تطبيق المصادقة',
      noApp:
        'لم يُفتح تطبيق مصادقة. ثبّت Google Authenticator أو Microsoft Authenticator ثم اضغط الزر مرة أخرى، أو انسخ المفتاح وأدخله يدوياً.',
      key: 'المفتاح (لإدخاله يدوياً)',
      copyKey: 'نسخ المفتاح',
      keyCopied: 'نُسخ المفتاح. الصقه في تطبيق المصادقة.',
    },
    recovery: {
      copyAll: 'نسخ الرموز',
      copied:
        'نُسخت الرموز. الصقها في مكان آمن (مثل مدير كلمات المرور)، ثم انسخ أي كلمة أخرى حتى لا تبقى في الحافظة.',
    },
    forgot: {
      needsInternet: 'استعادة كلمة المرور تحتاج إلى إنترنت. حاول مرة أخرى عندما يتصل الهاتف.',
      notYet:
        'استعادة كلمة المرور من الهاتف ستعمل عندما يُفعَّل إرسال رسائل واتساب. إلى ذلك الحين اطلب من مديرك أو من قسم تقنية المعلومات إعادة تعيين كلمة المرور.',
    },
    sync: {
      checking: 'جارٍ فحص الاتصال…',
      online: 'متصل',
      offline: 'بدون إنترنت',
      offlinePending: 'بدون إنترنت — {{count}} عملية لم تُرسل بعد',
      pending: '{{count}} عملية لم تُرسل بعد',
      storeProblem:
        'لم تُنقل بيانات هذا الهاتف إلى قاعدته المشفّرة بعد. الإرسال مستمر، لكن لا تصل بيانات جديدة حتى ينجح النقل، وتُعاد المحاولة عند فتح التطبيق من جديد. لا تحذف التطبيق ولا تمسح بياناته — وأخبر قسم التقنية إن بقيت هذه الرسالة.',
      storeLost:
        'تعذّر فتح بيانات هذا الهاتف، فلا يُرسل منه شيء الآن. سجّل الدخول من جديد ليبدأ الهاتف قاعدة جديدة وتنزل بياناتك من جديد. تُبلَّغ الإدارة وقسم التقنية بأي عمليات لم تُرسل وقد تكون ضاعت.',
      storeKeyLost:
        'مفتاح قاعدة بيانات هذا الهاتف مفقود، فلا يمكن فتح ما حُفظ عليه. عند تسجيل الدخول يبدأ الهاتف قاعدة جديدة، وتُبلَّغ الإدارة وقسم التقنية بأي عمليات لم تُرسل وقد تكون ضاعت.',
      storeKeyError:
        'تعذّرت قراءة مفتاح قاعدة بيانات هذا الهاتف الآن، فلا يُرسل شيء. أغلق التطبيق وافتحه من جديد؛ إذا تكرّر ذلك ثلاث مرات يبدأ الهاتف قاعدة جديدة بعد تسجيل الدخول. لا تحذف التطبيق ولا تمسح بياناته.',
      signInAgain: 'تسجيل الدخول من جديد',
      storeUnreadable:
        'تعذّرت قراءة مجلد بيانات هذا الهاتف، فلا يُرسل شيء الآن. أغلق التطبيق وافتحه من جديد. لا تحذف التطبيق ولا تمسح بياناته — واتصل بقسم التقنية إن بقيت هذه الرسالة.',
    },
    home: {
      hello: 'أهلاً {{name}}',
      soon: 'ميزات العمل (الجدول، الحضور، الطلبات، قسائم الراتب) تأتي في المراحل القادمة.',
      savedOffline: 'بدون إنترنت: تُعرض آخر بيانات محفوظة على هذا الهاتف.',
    },
    tabs: {
      home: 'الرئيسية',
      myWork: 'عملي',
      work: 'الإدارة',
      more: 'المزيد',
    },
    phase: 'تأتي في المرحلة {{phase}}',
    myWork: {
      title: 'عملي',
      intro: 'هنا ستجد جدولك وحضورك وطلباتك وقسائم راتبك.',
      schedule: 'جدول الدوام',
      attendance: 'الحضور والانصراف',
      requests: 'الطلبات (إجازة، سلفة)',
      payslips: 'قسائم الراتب',
    },
    work: {
      title: 'الإدارة',
      intro: 'أدوات عملك حسب دورك. تُبنى في مراحل المشروع القادمة.',
      approvals: 'الموافقات',
      reports: 'التقارير',
      finance: 'المالية',
      warehouse: 'المخزن',
      hr: 'الموارد البشرية',
      quality: 'الجودة',
      audit: 'التدقيق',
      branch: 'الفرع',
      deliveries: 'التوصيل',
      waste: 'الهدر والتالف',
      wasteDetail: 'سجّله حتى بدون إنترنت',
      receive: 'استلام بضاعة',
      receiveDetail: 'اعدد ما وصل، حتى بدون إنترنت',
      handoff: 'صوّر للحاسبة',
      handoffDetail: 'صورة فاتورة أو تالف لشاشة الحاسبة، عبر رمزها',
    },
    waste: {
      title: 'تسجيل هدر أو تالف',
      intro:
        'يُحفظ على هذا الهاتف ويُرسل حين يتوفر الإنترنت، مرة واحدة. لا تظهر الكميات المتوفرة هنا.',
      location: 'الموقع',
      chooseLocation: 'اختر الموقع.',
      noPlace: 'لا يوجد على هذا الهاتف موقع تسجّل فيه الهدر.',
      item: 'المادة',
      search: 'ابحث عن مادة بالاسم أو الرمز',
      noItem: 'لا مادة بهذا الاسم في هذا الموقع.',
      batch: 'الدفعة',
      batchOption: '{{code}} — تنتهي {{expiry}}',
      quantity: 'الكمية ({{unit}})',
      unitG: 'كغم',
      unitML: 'لتر',
      unitPC: 'قطعة',
      addItem: 'أضف مادة واحدة على الأقل.',
      addLine: 'إضافة مادة أخرى',
      removeLine: 'حذف هذه المادة',
      reason: 'السبب',
      reasonEXPIRED: 'منتهية الصلاحية',
      reasonDAMAGED: 'تالفة',
      reasonSPOILED: 'فاسدة',
      reasonOTHER: 'سبب آخر',
      chooseReason: 'اختر السبب.',
      reasonText: 'اكتب السبب',
      reasonTextRequired: 'اكتب السبب (3 أحرف على الأقل).',
      note: 'ملاحظة (اختياري)',
      photos: 'الصور',
      takePhoto: 'التقاط صورة',
      photoCount: 'الصور: {{count}}',
      removePhoto: 'حذف الصورة',
      noCameraPermission:
        'لا يمكن التصوير: اسمح لتطبيق دوب ستاف باستخدام الكاميرا من إعدادات الهاتف.',
      save: 'سجّل الهدر',
      saved: 'حُفظ الهدر على هذا الهاتف. يُرسل حين يتوفر الإنترنت، مرة واحدة.',
      notValid: 'تعذّر تجهيز الهدر. راجع الحقول.',
      sent: 'ما سجّلته من هذا الهاتف',
      nothingYet: 'لا شيء بعد.',
      lines: 'المواد: {{count}}',
      stateWaiting: 'بانتظار الإرسال',
      stateArrived: 'وصل',
      stateRefused: 'رُفض: {{reason}}',
      photosWaiting: 'صور لم تُرسل بعد: {{count}}',
      photosRefused: 'صور رفضها الخادم: {{count}} — أخبر قسم التقنية',
      missingTitle: 'لم يُسجَّل الهدر بعد. ينقص:',
      lineN: 'المادة {{n}}',
      quantityField: 'الكمية',
      photosProblem: 'الصور لا تُرسل منذ {{minutes}} دقيقة والهاتف متصل بالإنترنت.',
      photosProblemStep_link: 'توقفت عند طلب رابط الرفع من الخادم.',
      photosProblemStep_upload: 'توقفت عند رفع الصورة إلى التخزين.',
      photosProblemStep_confirm: 'توقفت عند طلب تأكيد الخادم.',
      photosProblemHint:
        'الصور محفوظة على هذا الهاتف ولن تُحذف، ويحاول الهاتف كل دقيقة. إن بقيت هكذا أخبر قسم التقنية بهذا السبب:',
    },
    // W5-6 (O68): the camera check, opened by a link only (dobstaff://camera-check).
    cameraCheck: {
      title: 'فحص الكاميرا',
      intro: 'يجرّب الكاميرا كما يستعملها التطبيق، ولا يرسل شيئاً ولا يحفظ الصور.',
      photo: 'تجربة تصوير صورة',
      qr: 'تجربة قارئ الرمز',
    },
    // W5-6: «صوّر للحاسبة» — the photo for a page's QR code, never kept on the phone.
    handoff: {
      title: 'صوّر للحاسبة',
      intro: 'وجّه الكاميرا إلى الرمز الظاهر على شاشة الحاسبة.',
      noneKept: 'تُرسل الصور مباشرة، ولا تُحفظ على هذا الهاتف.',
      allowCamera: 'السماح باستخدام الكاميرا',
      notOurs: 'هذا ليس رمز «صوّر بالهاتف» من نظام دوب.',
      purposeINVOICE: 'صورة فاتورة',
      purposeDAMAGE: 'صور بضاعة تالفة',
      forPlace: 'لـ {{place}}',
      notHere: 'هذا الرمز لموقع لا تعمل فيه، وقد يُرفض.',
      validFor: 'الرمز صالح لمدة {{minutes}}:{{seconds}}',
      expired: 'انتهت صلاحية الرمز. اعرض رمزاً جديداً على الحاسبة.',
      take: 'التقاط صورة',
      takeMore: 'التقاط جزء آخر',
      sending: 'جارٍ الإرسال…',
      arrived: 'وصلت الصورة {{n}}. انظر إلى شاشة الحاسبة.',
      offline: 'لا يوجد إنترنت — الصورة لا تُحفظ على الهاتف. صوّر عندما يعود الاتصال.',
      refused: 'رُفضت: {{reason}}',
      failed: 'لم تصل الصورة. حاول مرة أخرى. السبب: {{reason}}',
      another: 'قراءة رمز آخر',
    },
    // W5-5: the blind receipt on the phone (the form's words come from `receiving`).
    receive: {
      title: 'استلام بضاعة',
      intro:
        'فواتير تنتظر وصول بضاعتها. اعدد ما وصل فعلاً: لا تظهر هنا كميات الفاتورة ولا أسعارها. يُحفظ العدّ على هذا الهاتف ويُرسل حين يتوفر الإنترنت، مرة واحدة.',
      place: 'المخزن',
      noPlace: 'لا يوجد على هذا الهاتف مخزن تستلم فيه.',
      taskDetail: 'فاتورة {{number}} — {{date}}',
      open: 'اعدد ما وصل',
      unitOption: '{{name}} ({{factor}})',
      day: 'اليوم',
      month: 'الشهر',
      year: 'السنة',
      dateInvalid: 'هذا التاريخ غير صحيح. اكتب اليوم والشهر والسنة بالأرقام.',
      lineMissing: 'كل مادة في الفاتورة تحتاج عدّاً (0 إذا لم يصل منها شيء).',
      notValid: 'تعذّر تجهيز الاستلام. راجع الحقول.',
      notSaved:
        'لم يُحفظ الاستلام على هذا الهاتف ولم يُرسل شيء. حاول مرة أخرى، وإن تكرر أخبر قسم التقنية.',
      noItem: 'لا مادة بهذا الاسم.',
      damagePhotos: 'صور التالف',
      missingTitle: 'لم يُحفظ الاستلام بعد. ينقص:',
      back: 'رجوع',
      saved: 'حُفظ الاستلام على هذا الهاتف. يُرسل حين يتوفر الإنترنت، مرة واحدة.',
      sent: 'ما استلمته من هذا الهاتف',
      sentLine: '{{supplier}} — فاتورة {{number}}',
      sentUnknown: 'استلام',
    },
    more: {
      title: 'المزيد',
      profile: 'ملفي',
      sessions: 'أجهزتي',
      settings: 'الإعدادات',
      password: 'تغيير كلمة المرور',
      alerts: 'تنبيهاتي',
    },
    alerts: {
      title: 'تنبيهاتي',
      none: 'لا توجد تنبيهات.',
      new: 'جديد',
    },
    profile: {
      title: 'ملفي',
      nameAr: 'الاسم بالعربية',
      nameEn: 'الاسم بالإنجليزية',
      phone: 'رقم الهاتف',
      roles: 'أدواري',
      twoFactorOn: 'التحقق بخطوتين مفعّل',
      twoFactorOff: 'التحقق بخطوتين غير مفعّل',
      passwordLong: 'كلمة مرورك يجب أن تكون 16 حرفاً على الأقل (حساب إدارة يدخل بدون إنترنت).',
    },
    sessions: {
      title: 'أجهزتي',
      intro: 'الأجهزة التي دخلت منها إلى حسابك. إذا رأيت جهازاً لا تعرفه فأخرجه وغيّر كلمة المرور.',
      thisPhone: 'هذا الهاتف',
      signedIn: 'الدخول: {{time}}',
      lastUsed: 'آخر استخدام: {{time}}',
      signOut: 'إخراج هذا الجهاز',
      signOutOthers: 'إخراج كل الأجهزة الأخرى',
      signedOut: 'أُخرج الجهاز.',
      signedOutOthers: 'أُخرجت الأجهزة الأخرى.',
      kindStaff: 'تطبيق دوب ستاف {{version}} — {{detail}}',
      kindDesktop: 'برنامج Dob Desktop {{version}}',
      kindBrowser: '{{browser}} على {{os}}',
      kindUnknown: 'جهاز غير معروف',
    },
    settings: {
      title: 'الإعدادات',
      restartTitle: 'يلزم إعادة تشغيل التطبيق',
      restartBody: 'تغيّرت اللغة. يُعاد تشغيل التطبيق مرة واحدة ليتغيّر اتجاه الشاشة.',
      restartNow: 'أعد التشغيل الآن',
    },
    password: {
      title: 'تغيير كلمة المرور',
    },
  },
} as const;

type Widen<T> = { readonly [K in keyof T]: T[K] extends string ? string : Widen<T[K]> };
/** The shape every language must follow (keys of the Arabic file, any string values). */
export type Messages = Widen<typeof ar>;
